<template>
	<div class="d-flex">
		<SnapshotNotes
			v-if="!hasFreeTrialEnded && isAuthenticated && artifactType === 'snapshot'"
			ref="snapshotNotesPanel"
			:notes="artifactNotes"
			:current-snapshot-id="currentArtifactId"
			@updatedNotes="updateArtifactNotes" />
		<v-chip v-if="lightBoxOpen && parentType === 'project'" color="#fff" class="lightbox-access">{{
			isViewOnly ? "Viewer" : "Editor"
		}}</v-chip>

		<ImageEditor
			v-if="!hasFreeTrialEnded && isAuthenticated && currentArtifactUrl && artifactType === 'snapshot' && !isGifSnapshot"
			ref="imageEditor"
			:is-editing="annotating"
			:persistence-pending="savingAnnotation"
			:conversation-id="currentArtifact?.conversationId"
			:target-image="currentArtifactUrl"
			:snapshot-id="currentArtifactId"
			:default-show-controls="true"
			:show-rotate="true"
			:is-async-app="false"
			@imageSaved="saveAnnotation"
			@saveUnchanged="reportAnnotationUnchanged"
			@discard="cancelAnnotation"></ImageEditor>

		<div
			class="d-flex flex-column flex-1 lightbox-container"
			:style="{
				display: display.smAndDown.value && $refs.thumbnails?.isOpen() ? 'none !important' : 'flex'
			}">
			<MediaLightboxToolbar
				v-if="isDesktop"
				class="lightbox-header"
				:style="{ visibility: !showControls ? 'hidden' : 'visible' }">
				<template #actions>
					<!-- LSP-20796: the shared toolbar actions carry no v-track; the desktop bindings log the directive's exact
					     payload, while the mobile copy below keeps its v-tracks, so each click logs exactly once. -->
					<MediaLightboxActions
						v-bind="desktopLightboxActions"
						:touch="isTouch"
						@tag="
							trackDesktopLightboxAction('photos-lightbox-tag-button', 'photos-lightbox-tag');
							openTagDialog();
						"
						@rotate="
							trackDesktopLightboxAction('photos-lightbox-rotate-button', 'photos-lightbox-rotate');
							handleRotate();
						"
						@annotate="
							trackDesktopLightboxAction('photos-lightbox-annotate-button', 'photos-lightbox-annotate');
							startAnnotation();
						"
						@caption="
							trackDesktopLightboxAction('photos-lightbox-caption-button', 'photos-lightbox-caption');
							editArtifactNotes();
						"
						@remove="
							trackDesktopLightboxAction('photos-lightbox-delete-button', 'photos-lightbox-delete');
							deleteArtifact();
						">
						<template #after-annotate>
							<v-tooltip
								v-if="showMeasureButton"
								location="bottom"
								content-class="lightbox-tooltip"
								:open-on-click="!isMobile">
								<template #activator="{ props }">
									<v-btn
										v-test="'photos-lightbox-measure'"
										v-track="'photos-lightbox-measure-button'"
										icon
										variant="text"
										v-bind="props"
										@click="measureDialogVisible = true">
										<v-icon>mdi-measure-stroke</v-icon>
									</v-btn>
								</template>
								<span>Measure</span>
							</v-tooltip>
						</template>
						<template #after-caption>
							<v-tooltip
								v-if="canCopyArtifactLink"
								location="bottom"
								content-class="lightbox-tooltip"
								:open-on-click="!isMobile">
								<template #activator="{ props }">
									<v-btn
										icon
										variant="text"
										:disabled="isTempAnnotation"
										v-bind="props"
										@click="share(currentArtifactId)">
										<v-icon>icon-link-double</v-icon>
									</v-btn>
								</template>
								<span>{{ isTempAnnotation ? "The image is still processing" : "Copy Link" }}</span>
							</v-tooltip>
							<!-- LSP-21125: offered only when the live_translations flag is on and caption lines were saved for this
								 recording during the live call. Icon-only, like every other action in this row. -->
							<v-tooltip
								v-if="showTranslationToggle"
								location="bottom"
								content-class="lightbox-tooltip"
								:open-on-hover="!isTouch"
								:open-on-click="false"
								:open-on-focus="false">
								<template #activator="{ props }">
									<v-btn
										v-test="'lightbox-translation-toggle'"
										v-track="'photos-lightbox-translation-button'"
										icon
										variant="text"
										aria-label="Translation"
										:color="translationPanelOpen ? 'primary' : undefined"
										:active="translationPanelOpen"
										:aria-pressed="translationPanelOpen ? 'true' : 'false'"
										v-bind="props"
										@click="toggleTranslationPanel">
										<v-icon>mdi-translate</v-icon>
									</v-btn>
								</template>
								<span>{{ translationPanelOpen ? "Back to comments" : "Show the translated conversation" }}</span>
							</v-tooltip>
							<v-tooltip
								v-if="showSetProjectThumbnail"
								location="bottom"
								content-class="lightbox-tooltip"
								:open-on-click="!isMobile">
								<template #activator="{ props }">
									<v-btn
										v-test="'photos-lightbox-set-thumbnail'"
										elevation="0"
										icon
										variant="text"
										:disabled="isTempAnnotation"
										:loading="settingThumbnail"
										v-bind="props"
										@click="setAsProjectThumbnail">
										<v-icon size="24">mdi-image-outline</v-icon>
									</v-btn>
								</template>
								<span>{{ isTempAnnotation ? "The image is still processing" : "Set as project thumbnail" }}</span>
							</v-tooltip>
						</template>
						<template #before-delete>
							<v-tooltip
								v-if="showClipVideoButton"
								location="bottom"
								content-class="lightbox-tooltip"
								:open-on-click="!isMobile">
								<template #activator="{ props }">
									<v-btn
										elevation="0"
										icon
										variant="text"
										:disabled="isTempAnnotation"
										v-bind="props"
										@click="clipDialogVisible = true">
										<img :src="iconClipVideo" width="24" height="24" alt="Edit Video" />
									</v-btn>
								</template>
								<span>{{ isTempAnnotation ? "The image is still processing" : "Edit Video" }}</span>
							</v-tooltip>

							<v-tooltip
								v-if="!videoProcessing"
								location="bottom"
								content-class="lightbox-tooltip"
								:open-on-click="!isMobile">
								<template #activator="{ props }">
									<v-btn
										v-test="'photos-lightbox-download'"
										v-track="'photos-lightbox-download-button'"
										elevation="0"
										icon
										variant="text"
										:disabled="isArtifactUpdating || isTempAnnotation"
										:loading="downloadingArtifact"
										v-bind="props"
										@click="downloadArtifact">
										<v-icon>icon-download</v-icon>
									</v-btn>
								</template>
								<span>{{ isTempAnnotation ? "The image is still processing" : "Download" }}</span>
							</v-tooltip>
						</template>
					</MediaLightboxActions>
				</template>
				<template #close>
				<v-btn
					v-test="'photos-lightbox-close'"
					v-track="'photos-lightbox-close-button'"
					icon
					size="small"
					class="mr-2"
					color="black"
					@click="closeLightBox">
					<v-icon>icon-close-medium</v-icon>
				</v-btn>
				</template>
			</MediaLightboxToolbar>
			<div v-else-if="!annotating && !commenting" class="d-flex align-center justify-space-between pa-4">
				<div></div>

				<div
					v-if="isAuthenticated && getUserInitials() !== '-'"
					v-test="'photos-lightbox-user-details'"
					class="d-flex flex-row align-center">
					<v-avatar v-test="'photos-lightbox-user-details'" :color="getColor(currentArtifact?.agentAvatarColor)">
						<span class="team-avatar-text" :style="{ color: getReadableInitialsColor(currentArtifact?.agentAvatarColor) }">{{
							getUserInitials()
						}}</span>
					</v-avatar>
					<h6 class="ml-2 chip-text">{{ user?.name }}</h6>
				</div>
				<v-btn
					v-test="'photos-lightbox-close'"
					v-track="'photos-lightbox-close-button'"
					icon
					variant="plain"
					@click="closeLightBox">
					<v-icon>icon-close-medium</v-icon>
				</v-btn>
			</div>

			<!-- leaving the 72px as it is for DT but take 100% on mobiles -->
			<div
				v-if="!annotating"
				:style="{ height: isMobile ? '100%' : 'calc(100% - 72px)' }"
				class="d-flex flex-column"
				:class="{ 'justify-center': isDesktop }">
				<div style="height: 100%" class="d-flex flex-column align-center">
					<v-carousel
						v-if="currentArtifactId"
						ref="carousel"
						v-model="currentArtifactId"
						class="recording-carousel"
						hide-delimiter-background
						:height="isMobile ? 'auto' : '100%'"
						delimiter-icon="icon-round-solid"
						:style="isCommentBoxOpen && isMobile ? { visibility: 'hidden', position: 'absolute', pointerEvents: 'none' } : {}"
						:hide-delimiters="true"
						:show-arrows="isDesktop && artifactList.length > 1"
						@update:modelValue="artifactChanged">
						<template #prev="{ props }">
							<v-btn color="white" elevation="2" v-bind="props"></v-btn>
						</template>
						<template #next="{ props }">
							<v-btn color="white" elevation="2" v-bind="props"></v-btn>
						</template>
						<!-- LSP-21973: only the current slide, its neighbours and the outgoing slide are carousel items. -->
						<v-carousel-item
							v-for="artifact in carouselArtifacts"
							:key="artifact.id"
							:eager="true"
							:value="artifact.id"
							:transition="slideTransition"
							:reverse-transition="slideTransition">
							<div
								class="d-flex align-center flex-column"
								:class="{ 'justify-center': isDesktop }"
								style="width: 100%; height: 100%">
								<!-- TODO: Removed from style below, causing spacing issue: overflow: !isLandscape ? 'hidden' : 'scroll', -->
								<div
									class="d-flex flex-row justify-center"
									:style="{
										width: isMobile && isLandscape ? '85%' : '90%',
										maxHeight: isMobile ? '100%' : '80%',
										minHeight: isMobile ? '100%' : '0',
										flex: isMobile ? 'none' : '1 1 auto'
									}">
									<!--Snapshot-->
									<!-- LSP-21116: on mobile this container IS the media frame. It takes the same fixed px height as the
										 video container below, and the photo inside fills it (letterboxed) instead of sizing itself to 16:9. -->
									<MediaLightboxFrame
										v-if="artifactType === 'snapshot'"
										v-show="!annotating"
										class="d-flex snapshot-container"
										:with-sidebar="showCommentBox && !isMobile"
										:height="lightboxMediaFrameHeight"
										:style="{
											width: isMobile ? '100%' : showCommentBox ? '65%' : '100%',
											'min-width': isMobile ? '100%' : 'unset',
											flex: 1
										}">
										<ZoomablePhoto
											v-if="artifact.id === currentArtifactId"
											:src="artifact.mediaUrl"
											:lazy-src="artifact.thumbnailUrl"
											:alt="artifact.name || 'Media image'"
											:max-height="lightboxMediaMaxHeight"
											style="flex: 1; min-width: 0" />
										<!-- The eager preload image for every INACTIVE slide fills the same frame as the <ZoomablePhoto>
											 above. Both size to `.snapshot-container` and neither carries an aspect ratio of its own, so the
											 outgoing slide mid-transition and the displayed photo cannot disagree about how tall the frame is
											 (LSP-19030, LSP-21116). -->
										<v-img
											v-else
											:src="artifact.mediaUrl"
											:lazy-src="artifact.thumbnailUrl"
											style="width: 100%; height: 100%">
											<template #placeholder>
												<div class="d-flex align-center justify-center fill-height jerod-lb">
													<v-progress-circular color="grey-lighten-4" indeterminate></v-progress-circular>
												</div>
											</template>
										</v-img>
										<div
											v-if="artifact.updating"
											class="d-flex align-center justify-center fill-height jerod-lb loading-overlay"
											style="position: absolute; inset: 0">
											<v-progress-circular color="grey-lighten-4" indeterminate></v-progress-circular>
										</div>
									</MediaLightboxFrame>

									<div
										v-else-if="artifactType === 'recording'"
										class="d-flex flex-column"
										:style="{
											'min-width': isMobile ? '100%' : 'unset',
											width: isMobile ? '100%' : showCommentBox ? '90%' : '100%'
										}">
										<VideoProcessingState
											v-if="videoProcessing"
											ref="videoProcessing"
											refreshable
											:refreshing="isRefreshing"
											:style="{
												height: isMobile ? lightboxMediaFrameHeight : '100%',
												maxHeight: lightboxMediaMaxHeight,
												display: isCommentBoxOpen && isMobile ? 'none' : undefined
											}"
											@refresh="refreshProcessingMedia" />

										<!--Video-->
										<MediaLightboxFrame
											v-else
											id="video-container"
											class="d-flex"
											:with-sidebar="showCommentBox && !isMobile"
											:height="isMobile ? lightboxMediaFrameHeight : '100%'"
											:max-height="lightboxMediaMaxHeight"
											:style="{
												flex: isMobile ? 'none' : 1
											}">
											<VideoPlayer
												v-if="artifact.id === currentArtifactId"
												ref="videoPlayer"
												:content-class="currentArtifactId === artifact.id ? 'current-recording' : ''"
												:style="{ borderRadius: isMobile ? '12px' : '12px 0 0 0' }"
												:recording-id="artifact.id"
												:source="sourceWithTimestamp(artifact.mediaUrl, 0.1)"
												:can-snapshot="true"
												:should-pause="
													currentArtifactId === null ||
													currentArtifactId !== artifact.id ||
													commenting ||
													clipDialogVisible
												"
												:file-name="videoFileName(artifact)"
												:is-view-only="isViewOnly"
												:free-trial-ended="hasFreeTrialEnded"
												@playback-error="handlePlaybackError"
												@take-snapshot="takeSnapshot"
												@time-update="onRecordingTimeUpdate" />
										</MediaLightboxFrame>
									</div>
									<!-- TODO: Find a better solution for the 1px margin. The black background from the left container is showing and looks like a border -->
									<!--  -->
									<div
										v-show="showCommentBox || captureEvidenceExpanded || (isDesktop && translationPanelVisible)"
										v-if="currentArtifactId === artifact.id"
										class="comment-controls"
										:style="{
											width: (showCommentBox || captureEvidenceExpanded) && isMobile ? '90%' : '25%',
											height: (showCommentBox || captureEvidenceExpanded) && isMobile ? '100%' : 'auto',
											borderBottom: '2px solid rgb(243, 244, 245)'
										}"
										style="margin-left: 1px">
										<v-card class="desktop-right">
											<!-- LSP-18592: while Capture Evidence is expanded, this column shows its content (below) instead of
												 Comments. The v-show sits on each child rather than a wrapper div so the flags-off DOM is identical
												 to develop — a wrapper broke CommentBox's height:100% / flex:1 scroll chain under .desktop-right. -->
											<!-- LSP-15985: this panel carries the SAME `isDesktop || commenting` gate as the
												 CommentBox below it. Without it, saving an annotation on mobile flipped
												 isTempAnnotation true and rendered "The image is still processing" into a column
												 only ever meant to be visible on desktop. That gave the flex item a real
												 min-content width, and because the sibling snapshot column is pinned at
												 `min-width: 100%` on mobile the row overflowed - `justify-center` split the
												 overflow both ways, shifting the photo left out of the clipped v-window and
												 leaving a white sliver on the right. It self-healed once the real artifact
												 arrived, which is why it read as a transient rendering glitch. -->
											<div
												v-if="isTempAnnotation && (isDesktop || commenting)"
												v-show="!captureEvidenceExpanded && !translationPanelVisible"
												v-test="'lightbox-temp-annotation-processing'"
												class="d-flex flex-column ga-4 pl-2 pr-2 pt-4"
												:class="{ 'lightbox-hidden-by-translation': translationPanelVisible }">
												<div class="d-flex align-center justify-space-between">
													<v-card-title class="pa-0 d-flex align-baseline">
														<div class="d-flex media-header">
															<v-icon>icon-chat-stroke</v-icon>
															<span class="heading">Comments (0)</span>
														</div>
													</v-card-title>
												</div>
												<v-tooltip location="top">
													<template #activator="{ props: tooltipProps }">
														<div
															v-bind="tooltipProps"
															class="d-flex align-center justify-center pa-4 text-medium-emphasis">
															The image is still processing
														</div>
													</template>
													<span>The image is still processing</span>
												</v-tooltip>
											</div>
											<CommentBox
												v-else-if="isDesktop || commenting"
												v-show="!captureEvidenceExpanded && !translationPanelVisible"
												class="pl-2 pr-2 pt-4"
												:class="{ 'lightbox-hidden-by-translation': translationPanelVisible }"
												:parent-id="artifact.id"
												:parent-type="commentParentType"
												:project-id="projectId || project?.id"
												:project-access-denied-handled-globally="projectAccessDeniedHandledGlobally"
												:share-id="shareId"
												:conversation-id="artifact?.conversationId"
												:is-view-only="isViewOnly"
												:has-free-trial-ended="hasFreeTrialEnded"
												:light-box-view="true"
												:users="localUsers"
												:me="localMe"
												@toggle-reply-box="toggleReplyBox"
												@close-comment-box="startComment" />
											<CaptureEvidenceDebugPanel
												v-if="
													captureEvidenceDebugEnabled &&
													captureEvidenceExpanded &&
													(artifactType === 'snapshot' || artifactType === 'recording')
												"
												hide-toggle
												:expanded="captureEvidenceExpanded"
												:source-id="artifactId"
												:source-type="artifactType === 'recording' ? 'recording' : 'snapshot'"
												class="pl-2 pr-2 pt-4" />
											<!-- LSP-21125: same swap as Capture Evidence above — the column shows the saved caption lines
												 instead of Comments while the Translation toggle is on. -->
											<RecordingTranslationPanel
												v-if="isDesktop && translationPanelVisible && currentRecordingCaptions"
												:captions="currentRecordingCaptions"
												:current-time="recordingPlaybackSeconds"
												class="pl-2 pr-2 pt-4 pb-2"
												@seek="seekCurrentRecording"
												@close="closeTranslationPanel" />
										</v-card>
									</div>
								</div>
								<div
									v-if="isDesktop"
									class="d-flex flex-column pa-4"
									style="
										width: 90%;
										background-color: #fff;
										border-bottom-left-radius: 12px;
										border-bottom-right-radius: 12px;
									">
									<div
										v-if="shouldShowIncompleteBanner(artifact)"
										class="mb-4"
										v-test="'artifact-incomplete-upload-banner'">
										<IncompleteUploadBanner
											v-if="canSendUploadReminder(artifact)"
											:conversation-id="artifact.conversationId"
											:has-customer="!!artifact.customerId"
											:dismissible="false" />
										<v-alert v-else color="error" variant="outlined" border="start" icon="icon-warning-triangle">
											{{ incompleteUploadMessage(artifact) }}
										</v-alert>
									</div>
									<div
										v-if="isAuthenticated && isDesktop && getUserInitials() !== '-'"
										class="d-flex flex-row align-center">
										<v-avatar
											v-test="'photos-lightbox-user-details'"
											:color="getColor(currentArtifact?.agentAvatarColor)">
											<span
												class="team-avatar-text"
												:style="{ color: getReadableInitialsColor(currentArtifact?.agentAvatarColor) }"
												>{{ getUserInitials() }}</span
											>
										</v-avatar>
										<h6 class="ml-2 chip-text">{{ user?.name }}</h6>
									</div>
									<div v-test="'photos-lightbox-date'" class="d-flex align-center mt-2" style="color: rgba(0, 0, 0, 0.6)">
										{{ artifactDate }}
										<v-btn
											v-if="canEditDate"
											variant="plain"
											icon="mdi-pencil"
											density="compact"
											class="ml-1"
											@click="openDateEditDialog">
										</v-btn>
									</div>
									<SnapshotSourceDebugPreview v-if="artifactType === 'snapshot'" :snapshot-id="artifactId" class="mt-2" />
									<CaptureEvidenceDebugPanel
										v-if="captureEvidenceDebugEnabled && (artifactType === 'snapshot' || artifactType === 'recording')"
										hide-content
										:expanded="artifact.id === currentArtifactId ? captureEvidenceExpanded : false"
										:source-id="artifactId"
										:source-type="artifactType === 'recording' ? 'recording' : 'snapshot'"
										@update:expanded="
											(value) => {
												if (artifact.id === currentArtifactId) captureEvidenceExpanded = value;
											}
										" />
									<div
										v-if="currentArtifact?.notes"
										v-test="'photos-lightbox-description'"
										:title="currentArtifact?.notes"
										class="mt-2 text-break text-wrap overflow-hidden line-clamp-2"
										style="color: rgba(0, 0, 0, 0.6)">
										{{ currentArtifact?.notes }}
									</div>
									<div v-if="me?.organization" class="my-2">
										<SingleLineDisplay
											:value="currentArtifact?.Labels ?? []"
											:show-badge="true"
											:closeable="false"
											:max-visible="3"
											:absolute-badge="true" />
									</div>
								</div>
							</div>
						</v-carousel-item>
					</v-carousel>
					<!-- Mobile Custom Carousel Navigation -->
					<div
						v-if="isMobile && artifactList.length > 1"
						class="d-flex justify-center align-center py-2"
						:class="{ 'px-5': !isLandscape }"
						:style="{
							width: isLandscape ? '85%' : '100%',
							display: isCommentBoxOpen && isMobile ? 'none !important' : 'flex'
						}">
						<div class="d-flex justify-space-between align-center w-100 px-0">
							<v-btn
								icon
								:disabled="artifactList.length <= 1"
								class="carousel-nav-btn mr-4"
								@click="previousArtifact"
								elevation="2">
								<v-icon>icon-chevron-left</v-icon>
							</v-btn>

							<div class="text-body-1 text-center">{{ currentArtifactIndex + 1 }} of {{ artifactList.length }}</div>

							<v-btn
								icon
								:disabled="artifactList.length <= 1"
								class="carousel-nav-btn ml-4"
								elevation="2"
								@click="nextArtifact">
								<v-icon>icon-chevron-right</v-icon>
							</v-btn>
						</div>
					</div>

					<!-- Mobile Controls -->
					<div
						v-if="display.smAndDown.value"
						class="d-flex flex-column"
						:class="{ 'px-4': !isLandscape }"
						:style="{ width: isLandscape ? '85%' : '100%' }">
						<!-- LSP-17324: the warning at NARROW widths. Its twin sits in the metadata block below, which is
						     `v-if="isDesktop"`, so without this one a halved window, a tablet or a phone hid the warning
						     entirely and an incomplete recording looked complete — the original complaint.
						     Placed INSIDE this `smAndDown` controls block deliberately, after two attempts that failed for
						     opposite reasons: beside the desktop container it never rendered at all (that region is not
						     what draws here), and directly after <VideoPlayer> it rendered INSIDE the player's own box and
						     overlaid the video. This block is the one the narrow-width layout actually paints, so the
						     alert lands in normal flow above the controls instead of on top of the picture.
						     Reads `currentArtifact`, not `artifact`: this controls bar sits OUTSIDE the per-artifact v-for,
						     so the loop variable is not in scope here — referencing it rendered a Vue warning on every
						     mount and told four unrelated lightbox tests that something was wrong.
						     Same predicate and copy as the desktop instance; exactly one of the two renders. -->
						<div
							v-if="shouldShowIncompleteBanner(currentArtifact)"
							class="mt-3 mb-2"
							v-test="'artifact-incomplete-upload-banner-compact'">
							<IncompleteUploadBanner
								v-if="canSendUploadReminder(currentArtifact)"
								:conversation-id="currentArtifact.conversationId"
								:has-customer="!!currentArtifact.customerId"
								:dismissible="false" />
							<v-alert v-else color="error" variant="outlined" border="start" icon="icon-warning-triangle" density="compact">
								{{ incompleteUploadMessage(currentArtifact) }}
							</v-alert>
						</div>
						<div
							class="d-flex flex-column justify-space-between mobile-controls"
							:class="{ 'pt-2': isLandscape }"
							:style="{
								display: isCommentBoxOpen ? 'none !important' : 'flex'
							}">
							<div class="d-flex flex-row justify-space-between align-space-between">
								<div class="d-flex flex-row ga-0 mobile-controls-buttons">
									<div v-if="showCommentBox">
										<v-tooltip location="bottom" content-class="lightbox-tooltip">
											<template #activator="{ props }">
												<v-btn
													v-test="'photos-lightbox-comment'"
													v-track="'photos-lightbox-comment-button'"
													icon="icon-chat-stroke"
													variant="plain"
													:disabled="isTempAnnotation"
													v-bind="props"
													@click="startComment">
												</v-btn>
											</template>
											<span>{{ isTempAnnotation ? "The image is still processing" : "Comments" }}</span>
										</v-tooltip>
									</div>
									<!-- LSP-21125: the phone-width Translation toggle, beside Comments. Same test id as the desktop
										 one; the two rows never render together. -->
									<v-tooltip
										v-if="showTranslationToggle"
										location="bottom"
										content-class="lightbox-tooltip"
										:open-on-hover="!isTouch"
										:open-on-click="false"
										:open-on-focus="false">
										<template #activator="{ props }">
											<v-btn
												v-test="'lightbox-translation-toggle'"
												v-track="'photos-lightbox-translation-button'"
												variant="plain"
												icon="mdi-translate"
												aria-label="Translation"
												:color="translationPanelOpen ? 'primary' : undefined"
												:active="translationPanelOpen"
												:aria-pressed="translationPanelOpen ? 'true' : 'false'"
												v-bind="props"
												@click="toggleTranslationPanel">
											</v-btn>
										</template>
										<span>{{
											translationPanelOpen ? "Hide the translated conversation" : "Show the translated conversation"
										}}</span>
									</v-tooltip>
									<v-tooltip
										v-if="showEdit"
										location="bottom"
										content-class="lightbox-tooltip"
										:disabled="isGifSnapshot ? false : isTouch"
										:open-on-hover="!isTouch"
										:open-on-click="isGifSnapshot && isTouch"
										:open-on-focus="isGifSnapshot">
										<template #activator="{ props }">
											<span
												v-test="'photos-lightbox-annotate-tooltip'"
												v-bind="props"
												class="d-inline-flex"
												:tabindex="isGifSnapshot ? 0 : undefined"
												:role="isGifSnapshot ? 'button' : undefined"
												:aria-disabled="isGifSnapshot || undefined"
												:aria-label="isGifSnapshot ? 'Annotate' : undefined">
												<v-btn
													v-test="'photos-lightbox-annotate'"
													v-track="'photos-lightbox-annotate-button'"
													variant="plain"
													icon="icon-pencil-edit-stroke"
													:disabled="isGifSnapshot || isArtifactUpdating || isTempAnnotation"
													aria-label="Annotate"
													:aria-hidden="isGifSnapshot || undefined"
													@click="startAnnotation">
												</v-btn>
											</span>
										</template>
										<span>{{ imageAnnotationUnavailableReason || "Annotate" }}</span>
									</v-tooltip>
									<v-tooltip
										v-if="showMeasureButton"
										location="bottom"
										content-class="lightbox-tooltip"
										:open-on-hover="!isTouch"
										:open-on-click="false"
										:open-on-focus="false">
										<template #activator="{ props }">
											<v-btn
												v-test="'photos-lightbox-measure'"
												v-track="'photos-lightbox-measure-button'"
												variant="plain"
												icon="mdi-measure-stroke"
												v-bind="props"
												@click="measureDialogVisible = true">
											</v-btn>
										</template>
										<span>Measure</span>
									</v-tooltip>
									<v-tooltip
										v-if="showCaption"
										location="bottom"
										content-class="lightbox-tooltip"
										:disabled="isGifSnapshot ? false : isTouch"
										:open-on-hover="!isTouch"
										:open-on-click="isGifSnapshot && isTouch"
										:open-on-focus="isGifSnapshot">
										<template #activator="{ props }">
											<span
												v-test="'photos-lightbox-edit-tooltip'"
												v-bind="props"
												class="d-inline-flex"
												:tabindex="isGifSnapshot ? 0 : undefined"
												:role="isGifSnapshot ? 'button' : undefined"
												:aria-disabled="isGifSnapshot || undefined"
												:aria-label="isGifSnapshot ? 'Add Caption' : undefined">
												<v-btn
													v-test="'photos-lightbox-edit'"
													v-track="'photos-lightbox-edit-button'"
													variant="plain"
													icon
													:disabled="isGifSnapshot || isTempAnnotation"
													aria-label="Add Caption"
													:aria-hidden="isGifSnapshot || undefined"
													@click="editArtifactNotes">
													<div class="icon pt-1">
														<img :src="liveswitchCaption" alt="Processing" />
													</div>
												</v-btn>
											</span>
										</template>
										<span>{{ imageEditingUnavailableReason || "Add Caption" }}</span>
									</v-tooltip>
									<v-tooltip
										v-if="canCopyArtifactLink"
										location="bottom"
										content-class="lightbox-tooltip"
										:open-on-hover="!isTouch"
										:open-on-click="false"
										:open-on-focus="false">
										<template #activator="{ props }">
											<v-btn
												icon="icon-link-double"
												variant="plain"
												:disabled="isTempAnnotation"
												v-bind="props"
												@click="share(currentArtifactId)">
											</v-btn>
										</template>
										<span>{{ isTempAnnotation ? "The image is still processing" : "Copy Link" }}</span>
									</v-tooltip>
									<v-tooltip
										v-if="showRotate"
										location="bottom"
										content-class="lightbox-tooltip"
										:disabled="isGifSnapshot ? false : isTouch"
										:open-on-hover="!isTouch"
										:open-on-click="isGifSnapshot && isTouch"
										:open-on-focus="isGifSnapshot">
										<template #activator="{ props }">
											<span
												v-test="'photos-lightbox-rotate-tooltip'"
												v-bind="props"
												class="d-inline-flex"
												:tabindex="isGifSnapshot ? 0 : undefined"
												:role="isGifSnapshot ? 'button' : undefined"
												:aria-disabled="isGifSnapshot || undefined"
												:aria-label="isGifSnapshot ? 'Rotate' : undefined">
												<v-btn
													v-test="'photos-lightbox-rotate'"
													v-track="'photos-lightbox-rotate-button'"
													elevation="0"
													icon
													variant="text"
													:disabled="isGifSnapshot || isArtifactUpdating || isTempAnnotation"
													aria-label="Rotate"
													:aria-hidden="isGifSnapshot || undefined"
													@click="handleRotate">
													<v-icon>icon-refresh</v-icon>
												</v-btn>
											</span>
										</template>
										<span>{{ imageRotationUnavailableReason || "Rotate" }}</span>
									</v-tooltip>
									<v-tooltip v-if="showTags" location="bottom" content-class="lightbox-tooltip">
										<template #activator="{ props }">
											<v-btn
												v-test="'photos-lightbox-tag'"
												v-track="'photos-lightbox-tag-button'"
												variant="plain"
												icon="icon-shop-tag-stroke"
												:disabled="isTempAnnotation"
												v-bind="props"
												@click="openTagDialog">
											</v-btn>
										</template>
										<span>{{ isTempAnnotation ? "The image is still processing" : "Manage Tags" }}</span>
									</v-tooltip>
									<v-tooltip
										v-if="showSetProjectThumbnail"
										location="bottom"
										content-class="lightbox-tooltip"
										:open-on-hover="!isTouch"
										:open-on-click="false"
										:open-on-focus="false">
										<template #activator="{ props }">
											<v-btn
												v-test="'photos-lightbox-set-thumbnail-mobile'"
												variant="plain"
												icon
												:disabled="isTempAnnotation"
												:loading="settingThumbnail"
												v-bind="props"
												@click="setAsProjectThumbnail">
												<v-icon size="24">mdi-image-outline</v-icon>
											</v-btn>
										</template>
										<span>{{ isTempAnnotation ? "The image is still processing" : "Set as project thumbnail" }}</span>
									</v-tooltip>
								</div>
								<div class="d-flex flex-row ga-0 mobile-controls-buttons">
									<div v-if="showClipVideoButton">
										<v-tooltip location="bottom" content-class="lightbox-tooltip">
											<template #activator="{ props }">
												<v-btn
													icon
													variant="plain"
													:disabled="isTempAnnotation"
													v-bind="props"
													@click="clipDialogVisible = true">
													<img :src="iconClipVideo" width="24" height="24" alt="Edit Video" />
												</v-btn>
											</template>
											<span>{{ isTempAnnotation ? "The image is still processing" : "Edit Video" }}</span>
										</v-tooltip>
									</div>
									<div v-if="!videoProcessing">
										<v-tooltip location="bottom" content-class="lightbox-tooltip">
											<template #activator="{ props }">
												<v-btn
													v-test="'photos-lightbox-download'"
													v-track="'photos-lightbox-download-button'"
													icon="icon-download"
													variant="plain"
													:disabled="isArtifactUpdating || isTempAnnotation"
													:loading="downloadingArtifact"
													v-bind="props"
													@click="downloadArtifact">
												</v-btn>
											</template>
											<span>{{ isTempAnnotation ? "The image is still processing" : "Download" }}</span>
										</v-tooltip>
									</div>
									<div v-if="showDelete">
										<v-tooltip location="bottom" content-class="lightbox-tooltip">
											<template #activator="{ props }">
												<v-btn
													v-test="'photos-lightbox-delete'"
													v-track="'photos-lightbox-delete-button'"
													icon
													variant="plain"
													:disabled="isTempAnnotation"
													v-bind="props"
													@click="() => deleteArtifact()">
													<v-icon color="red">icon-trash-full-stroke</v-icon>
												</v-btn>
											</template>
											<span>{{ isTempAnnotation ? "The image is still processing" : "Delete" }}</span>
										</v-tooltip>
									</div>
								</div>
							</div>
						</div>
						<div
							class="d-flex flex-column"
							:style="{
								backgroundColor: !isMobile ? '#fff' : 'transparent',
								borderRadius: isMobile ? '12px' : '0px 0px 12px 12px',
								display: (isCommentBoxOpen || translationPanelVisible) && isMobile ? 'none !important' : 'flex'
							}">
							<div v-test="'photos-lightbox-date'" class="d-flex align-center" style="color: rgba(0, 0, 0, 0.6)">
								{{ artifactDate }}
								<v-btn
									v-if="canEditDate"
									variant="plain"
									icon="mdi-pencil"
									density="compact"
									class="ml-1"
									@click="openDateEditDialog">
								</v-btn>
							</div>
							<SnapshotSourceDebugPreview v-if="artifactType === 'snapshot'" :snapshot-id="artifactId" class="mt-2" />
							<CaptureEvidenceDebugPanel
								v-if="captureEvidenceDebugEnabled && (artifactType === 'snapshot' || artifactType === 'recording')"
								:source-id="artifactId"
								:source-type="artifactType === 'recording' ? 'recording' : 'snapshot'" />
							<div
								v-if="currentArtifact?.notes"
								v-test="'photos-lightbox-description'"
								class="mt-2 text-break text-wrap overflow-hidden line-clamp-2"
								style="color: rgba(0, 0, 0, 0.6)">
								{{ currentArtifact?.notes }}
							</div>
							<div class="my-2">
								<SingleLineDisplay
									:value="currentArtifact?.Labels ?? []"
									:show-badge="true"
									:closeable="false"
									:max-visible="3"
									:absolute-badge="true" />
							</div>
						</div>
					</div>
					<!-- LSP-21125: at phone width the Translation panel takes the place of the date/caption/labels block, under
						 the controls row. Unlike mobile Comments it leaves the video on screen and playing: the rows follow
						 the playhead and seek it. Comments and the panel are mutually exclusive (toggleTranslationPanel /
						 startComment). -->
					<div
						v-if="isMobile && translationPanelVisible && currentRecordingCaptions"
						v-test="'lightbox-translation-panel-mobile'"
						class="d-flex flex-column mt-2 mobile-translation-panel"
						:class="{ 'px-4': !isLandscape }"
						:style="{ width: isLandscape ? '85%' : '100%' }">
						<v-card class="desktop-right">
							<RecordingTranslationPanel
								:captions="currentRecordingCaptions"
								:current-time="recordingPlaybackSeconds"
								close-label="Hide the translated conversation"
								class="pl-2 pr-2 pt-4 pb-2"
								@seek="seekCurrentRecording"
								@close="closeTranslationPanel" />
						</v-card>
					</div>
					<!-- TODO: Fix/restyle/restructure comments to avoid using VW for width if possible -->
					<div v-if="isMobile && currentArtifactId" v-show="isCommentBoxOpen" class="comment-controls" style="height: 100%">
						<v-card class="desktop-right">
							<CommentBox
								v-if="commenting && !isTempAnnotation"
								class="pl-2 pr-2 pt-4"
								:parent-id="currentArtifact?.id ?? artifactId"
								:parent-type="commentParentType"
								:is-view-only="isViewOnly"
								:has-free-trial-ended="hasFreeTrialEnded"
								:conversation-id="currentArtifact?.conversationId"
								:light-box-view="true"
								:project-id="projectId || project?.id"
								:project-access-denied-handled-globally="projectAccessDeniedHandledGlobally"
								:share-id="shareId"
								:users="localUsers"
								:me="localMe"
								@toggle-reply-box="toggleReplyBox"
								@close-comment-box="startComment" />
						</v-card>
					</div>
				</div>
			</div>
		</div>
		<TagSelectionDialog
			v-if="isAuthenticated"
			:dialog-visible="tagDialogVisible"
			:z-index="1000010"
			:saving="savingTags"
			:pre-selected-items="currentArtifact?.Labels ?? []"
			@cancel="tagDialogVisible = false"
			@agree="saveTags" />

		<SnapshotMeasureDialog
			v-if="showMeasureButton && currentArtifact?.mediaUrl"
			v-model:visible="measureDialogVisible"
			:measure="snapshotMeasure"
			:src="currentArtifact.mediaUrl"
			:lazy-src="currentArtifact.thumbnailUrl"
			alt="Photo to measure" />

		<ClipVideoDialog
			v-if="currentArtifact && artifactType === 'recording' && currentArtifact.mediaUrl"
			v-model:visible="clipDialogVisible"
			:video-source="sourceWithTimestamp(currentArtifact.mediaUrl, 0.1)"
			:recording-id="currentArtifact.id"
			:conversation-id="currentArtifact.conversationId"
			:project-id="currentArtifact.projectId"
			@saved="onClipSaved"
			@cancel="clipDialogVisible = false" />

		<!-- Date Edit Dialog -->
		<!--
			LSP-19247: `transition="fade-transition"` instead of Vuetify's default `dialog-transition`.
			The default scales the dialog content from 1 to 0.9 while it leaves, and Chrome paints an
			`input[type="date"]`'s native `::-webkit-calendar-picker-indicator` WITHOUT the ancestor
			transform — so for the ~125ms of the close animation the calendar/clock glyphs stayed put
			while the rest of the card shrank around them, which reads as the icon jumping to the right.
			A fade-only transition never sets a transform, so nothing can be painted out of sync.
		-->
		<v-dialog v-model="dateEditDialogVisible" max-width="480" :z-index="1000010" transition="fade-transition">
			<v-card rounded="xl" class="edit-date-dialog">
				<v-card-title class="d-flex align-center justify-space-between px-6 pt-5 pb-2">
					<div class="text-h6">Edit Date &amp; Time</div>
					<v-btn icon variant="text" size="small" :disabled="savingDate" @click="dateEditDialogVisible = false">
						<v-icon>icon-close-medium</v-icon>
					</v-btn>
				</v-card-title>

				<!--
					LSP-19248: padding lives in `.edit-date-dialog .v-card-text` below, NOT in `px-6 pt-2 pb-8`
					utilities. App.vue ships a GLOBAL `.v-card-text { padding: 0.5rem !important }` that lands
					after Vuetify's utility layer, so the utilities were silently collapsed to 8px while the
					header/actions (which have no such global override) kept their 24px — leaving the "Date"
					label 16px to the left of the "Edit Date & Time" title.
				-->
				<v-card-text class="edit-date-body">
					<div class="d-flex flex-column ga-4 edit-date-fields">
						<div class="d-flex flex-column ga-2">
							<div class="text-body-2 text-medium-emphasis font-weight-bold">Date</div>
							<v-date-picker
								v-model="editDatePickerModel"
								v-test="'edit-date-picker'"
								width="100%"
								control-variant="modal"
								hide-header
								color="primary"
								:min="editDateMinValue"
								:max="editDateMaxValue"
								:disabled="savingDate" />
						</div>

						<div class="d-flex flex-column ga-2">
							<div class="text-body-2 text-medium-emphasis font-weight-bold">Time</div>
							<TimeSelect v-model="editTimeValue" :disabled="savingDate" :error="!!editDateError" />
						</div>
					</div>
					<div v-if="editDateError" class="text-body-2 text-error mt-2" data-test="edit-date-error">
						{{ editDateError }}
					</div>
				</v-card-text>

				<v-card-actions class="d-flex justify-space-between ga-2 px-6 pb-5 pt-4 edit-date-actions">
					<v-btn color="grey" variant="outlined" rounded="xl" :disabled="savingDate" @click="dateEditDialogVisible = false">
						Cancel
					</v-btn>
					<!--
						LSP-20010 (epic LSP-16731): Save is NOT gated on validity or on "something changed" —
						it stays clickable and `saveArtifactDate` decides. `savingDate` is the one remaining
						disable, and it guards an in-flight request against a double submit, which
						CLAUDE.md -> "Form action buttons - always enabled, no-op when invalid" allows.
					-->
					<v-btn
						color="primary"
						variant="flat"
						rounded="xl"
						:disabled="savingDate"
						:loading="savingDate"
						@click="saveArtifactDate">
						Save
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script lang="ts">
	import api from "../api";
	import { auth } from "@/auth.js";
	import IncompleteUploadBanner from "@/components/IncompleteUploadBanner.vue";
	import EventBus from "@/helpers/eventBus";
	import timezones from "@/helpers/timezones";
	import DeviceUtility from "@/helpers/device";
	import { mapResultToLightBoxArtifact } from "@/helpers/filters";
	import {
		blurActiveElement,
		calculateTimeout,
		getAgentNameInitials,
		getColor,
		getReadableInitialsColor,
		getMessageFromError,
		sourceWithTimestamp
	} from "../helpers/utilities";
	import Account, { isRoleOrInRoles } from "../api/account";
	import SnapshotHelper from "../helpers/snapshot";
	import { EntityType } from "@/types/EntityType";
	import CommentBox from "./CommentBox.vue";
	import { ArtifactType } from "@/enums/ArtifactType";
	import { Role } from "../enums/Role";
	import SingleLineDisplay from "@/components/tags/SingleLineDisplay.vue";
	import TagSelectionDialog from "@/components/tags/TagSelectionDialog.vue";
	import { LightBoxArtifact } from "@/types/media/LightBoxArtifact";
	import { computed, markRaw } from "vue";
	import type { PropType } from "vue";
	import { User } from "@/types/User";
	import { generateFileName, downloadFile } from "@/helpers/download";
	import { Label } from "@/types/Label";
	import { MasterDocument } from "@/types/MasterDocument";
	import { MasterRecording } from "@/types/MasterRecording";
	import CameraShutterClickUrl from "@/assets/camera-shutter-click.mp3";
	import { RecordingStatus } from "@/types/artifacts/Recordings";
	import { isIncompleteRecordingUpload, type UploadStatusVerdicts } from "@/helpers/incompleteRecordingUpload";
	import { RecordingType } from "@/types/media/RecordingType";
	import { useDisplay } from "vuetify";
	import SnapshotNotes from "@/components/SnapshotNotes.vue";
	import SnapshotSourceDebugPreview from "@/components/SnapshotSourceDebugPreview.vue";
	import CaptureEvidenceDebugPanel from "@/components/CaptureEvidenceDebugPanel.vue";
	import RecordingTranslationPanel from "@/components/RecordingTranslationPanel.vue";
	import { hasCaptionLines } from "@/helpers/recordingCaptions";
	import type { RecordingCaptionsResponse } from "@/types/RecordingCaptions";
	import { isLoadedMeFeatureFlagEnabled, isMeFeatureFlagEnabled } from "@/helpers/meFeatureFlags";
	import SnapshotMeasureDialog from "@/components/measure/SnapshotMeasureDialog.vue";
	import { useSnapshotMeasure } from "@/composables/useSnapshotMeasure";
	import ImageEditor from "@common-ui/media/ImageEditor.vue";
	import TimeSelect from "@common-ui/components/TimeSelect.vue";
	import VideoPlayer from "@/components/VideoPlayer.vue";
	import ZoomablePhoto from "@common-ui/media/ZoomablePhoto.vue";
	import MediaLightboxToolbar from "@common-ui/media/MediaLightboxToolbar.vue";
	import MediaLightboxActions from "@common-ui/media/MediaLightboxActions.vue";
	import MediaLightboxFrame from "@common-ui/media/MediaLightboxFrame.vue";
	import { lightboxMobileMediaHeight } from "@common-ui/media/lightboxMediaSize";
	import ClipVideoDialog from "@/components/clip-video/ClipVideoDialog.vue";
	import iconClipVideo from "@/assets/icon-clip-video.svg";
	import liveswitchCaption from "@common-ui/assets/liveswitch-caption.svg";
	import VideoProcessingState from "@common-ui/media/VideoProcessingState.vue";
	import { useToast } from "vue-toastification";
	import type { IndexableDocument } from "@/types/search/IndexableDocument";
	import { logError, logInfo } from "@/helpers/logError";
	import { redactedUploadFailure } from "@/services/uploads/uploadLogRedaction";
	import { MEDIA_DATE_MIN_YEAR, validateMediaEditDate } from "@/helpers/mediaDateEdit";

	// LSP-20010: named so the diagnostic string and the "show the neutral message" branch cannot drift.
	const DATE_SAVE_UNCHANGED_REASON = "the date/time is unchanged";
	import { isUnauthenticatedError } from "@/types/errors/UnauthenticatedError";
	import { newUploadId } from "@uploads";
	import { couldNot } from "@/helpers/userMessages";
	import Logging from "../../plugin/track";

	type PlaybackRecoveryRecording = {
		id?: string;
		recordingUrl?: string;
		mediaUrl?: string;
		url?: string;
		thumbnailUrl?: string;
		privateThumbnailUrl?: string;
		MasterRecording?: { id?: string };
	};

	function normalizeIdentifier(value: unknown): string | undefined {
		return typeof value === "string" && value.trim().length > 0 ? value.trim() : undefined;
	}

	function isAuthStatusCode(statusCode: unknown): boolean {
		if (typeof statusCode === "number") {
			return statusCode === 401;
		}
		if (typeof statusCode === "string") {
			if (!/^\s*\d+\s*$/.test(statusCode)) {
				return false;
			}
			const parsedStatusCode = Number(statusCode);
			return parsedStatusCode === 401;
		}
		return false;
	}

	function isAuthRefreshFailure(error: unknown): boolean {
		if (isUnauthenticatedError(error)) {
			return true;
		}
		if (typeof error !== "object" || error === null) {
			return false;
		}
		const authError = error as {
			code?: unknown;
			status?: unknown;
			statusCode?: unknown;
			httpStatus?: unknown;
			response?: { status?: unknown };
			errorCode?: unknown;
			error?: unknown;
		};
		const statusCodes = [authError.code, authError.status, authError.statusCode, authError.httpStatus, authError.response?.status];
		if (statusCodes.some(isAuthStatusCode)) {
			return true;
		}
		const errorCode = `${authError.errorCode ?? authError.error ?? ""}`.trim().toUpperCase();
		return ["INVALID_TOKEN", "TOKEN_INVALID", "UNAUTHORIZED", "TOKEN_EXPIRED"].includes(errorCode);
	}
	export default {
		components: {
			TimeSelect,
			IncompleteUploadBanner,
			CommentBox,
			SingleLineDisplay,
			TagSelectionDialog,
			SnapshotNotes,
			SnapshotSourceDebugPreview,
			CaptureEvidenceDebugPanel,
			SnapshotMeasureDialog,
			ImageEditor,
			VideoPlayer,
			ZoomablePhoto,
			MediaLightboxToolbar,
			MediaLightboxActions,
			MediaLightboxFrame,
			VideoProcessingState,
			ClipVideoDialog,
			RecordingTranslationPanel
		},
		setup() {
			const display = useDisplay();
			const toast = useToast();
			// Tap-to-measure (web port of the mobile snapshot measure overlay). The `/me` flag is read
			// synchronously like captureEvidenceDebugEnabled: the object-crop route is authenticated,
			// and an unauthenticated share viewer has no loaded `me`, so it correctly reads as off.
			// capture_evidence is the master switch: nothing capture-evidence-related shows without it,
			// even if capture_measure_ui were ever sent on by itself.
			const captureMeasureUiEnabled =
				isLoadedMeFeatureFlagEnabled("capture_evidence") && isLoadedMeFeatureFlagEnabled("capture_measure_ui");
			const snapshotMeasure = useSnapshotMeasure();
			// True once the current photo is known to carry capture evidence (see snapshotMeasure.load).
			const measureAvailable = computed(() => !!snapshotMeasure.target.value);
			return { display, toast, getColor, getReadableInitialsColor, captureMeasureUiEnabled, snapshotMeasure, measureAvailable };
		},
		props: {
			modelValue: {
				type: Array as PropType<IndexableDocument[]>,
				required: true
			},
			bulkSelect: {
				type: Boolean,
				required: false
			},
			showGallery: {
				type: Boolean,
				default: true
			},
			isViewOnly: {
				type: Boolean,
				default: false
			},
			parentType: {
				type: String as PropType<EntityType>,
				default: "conversation"
			},
			projectId: {
				type: String,
				required: false
			},
			projectAccessDeniedHandledGlobally: {
				type: Boolean,
				default: false
			},
			shareId: {
				type: String,
				default: ""
			},
			project: {
				type: Object,
				required: false
			},
			parentId: {
				type: String,
				required: false
			},
			users: {
				type: Array as PropType<User[]>,
				default: () => [],
				required: false
			},
			me: {
				type: Object,
				default: null,
				required: false
			},
			hasFreeTrialEnded: {
				type: Boolean,
				required: true
			},
			// LSP-17772: caller is a share-origin editor from a DIFFERENT org. Rotate and delete
			// are own-uploads-only for them server-side (rotate replaces the image bytes; delete
			// removes another org's evidence) — the UI mirrors that instead of surfacing a 403.
			isExternalEditor: {
				type: Boolean,
				default: false
			},
			externalAccessResolved: {
				type: Boolean,
				default: true
			},
			/**
			 * F-02 residual (media-upload resilience review): the server's own "can a reminder be sent for
			 * this conversation AT ALL?" verdict, when the MOUNT already holds it.
			 *
			 * Every send path refuses a non-ASYNC conversation with 422, so on a SYNC/live session the
			 * Send Reminder button here can only ever fail — the same dead control the session page's
			 * banner stopped offering. This component cannot derive the verdict itself: it comes from
			 * `GET /conversations/:id/upload-status`, and nothing in `LightBoxArtifact` (which is mapped
			 * from a SEARCH INDEX document) carries the conversation type.
			 *
			 * So the verdict is threaded in, and it is CONVERSATION-SCOPED rather than a bare boolean: this
			 * component is also mounted from ProjectChecklists and SearchResults, and even on a session page
			 * the carousel can span conversations. A verdict only applies to the artifact whose
			 * `conversationId` it names.
			 *
			 * Null — every mount that cannot supply one, and every conversation it does not name — keeps
			 * today's behavior exactly: the control is offered, subject to the local gates below. Failing
			 * OPEN is deliberate; hiding a live, working button on every mount that has no verdict would be
			 * a far worse regression than the dead button this removes on the one mount that does.
			 */
			uploadReminderVerdict: {
				type: Object as PropType<{ conversationId: string; canSendUploadReminder: boolean } | null>,
				default: null
			},
			/**
			 * LSP-17328: the session page's GET /conversations/:id/upload-status response, so the incomplete-upload
			 * notice reads the SAME per-item server `abandoned` answer (`items`) the session banner does. Null on every mount that
			 * has none, and on a response without `items`: the shared predicate then falls back to the index flag exactly as before.
			 */
			uploadStatus: {
				type: Object as PropType<UploadStatusVerdicts>,
				default: null
			}
		},
		data() {
			return {
				liveswitchCaption,
				iconClipVideo,
				sourceWithTimestamp,
				saving: false,
				// LSP-16827: visual feedback for the "video still processing" Refresh button.
				// The refresh is fire-and-forget (we emit `load-recordings` and the parent
				// reloads), so on mobile a tap with no spinner reads as a dead button.
				isRefreshing: false,
				snapshotList: [],
				artifactList: [] as LightBoxArtifact[],
				artifactNotes: "",
				lightBoxIndex: null as number | null,
				lightBoxOpen: false,
				annotating: false,
				commenting: false,
				// LSP-18592: when true, the desktop right-hand column shows the Capture Evidence
				// panel (teleported there) instead of Comments, so it gets that column's full
				// scrollable height instead of the cramped strip under the photo.
				captureEvidenceExpanded: false,
				// LSP-18592 go-live: the inspector is not even mounted unless ?debug=true AND the loaded `me`
				// says capture_evidence is on, so flag-off admin runs none of its code (and never calls getMe).
				captureEvidenceDebugEnabled:
					new URLSearchParams(window.location.search).get("debug") === "true" && isLoadedMeFeatureFlagEnabled("capture_evidence"),
				// LSP-21125: the Translation toggle. It stays on across gallery navigation, but the panel only
				// shows for a recording that has lines (`translationPanelVisible`); others fall back to Comments.
				translationPanelOpen: false,
				// Keyed by MasterRecording id. A key that is present was read: the response when it has lines,
				// null when it has none or the read failed (the panel is optional — no retry, no error UI).
				recordingCaptionsByMasterRecordingId: {} as Record<string, RecordingCaptionsResponse | null>,
				// In-flight reads, so re-fires of the currentArtifact watcher (artifact list refreshes) do not
				// duplicate a request. markRaw: request bookkeeping must not drive rendering.
				pendingRecordingCaptionIds: markRaw(new Set<string>()),
				// Artifacts already reported as lacking a MasterRecording id — log once per artifact, not per render.
				reportedMissingMasterRecordingArtifactIds: markRaw(new Set<string>()),
				// The current recording's playhead (seconds), from VideoPlayer's `time-update`. Tracked ONLY while the
				// Translation panel is visible (its sole reader), so playback does not re-render the lightbox otherwise.
				recordingPlaybackSeconds: 0,
				// LSP-16267: set of commentIds whose reply view is currently open. Each
				// CommentAction owns its own showReplyBox, and CommentsList allows several
				// to be open at once, so a single boolean would drift (open A, open B, close
				// A → flag=false while B is still open). The reply-box-open check in
				// `handleKeydown` reads `replyBoxOpen` (computed) which is `size > 0`.
				openReplyBoxIds: new Set<string>(),
				savingAnnotation: false,
				annotationSavePendingFeedbackShown: false,
				pendingRemoteArtifactRemovalIds: markRaw(new Set<string>()),
				isTouch: false,
				dirty: false,
				commentBoxStyle: {}, // { display: "none" },
				isLightboxThumbsOpen: false,
				isAuthenticated: false,
				backgroundImage: "",
				getAgentNameInitials,
				sourceCommentWidth: -1,
				backgroundImageCaption: {
					title: "",
					description: ""
				},
				isImageChanging: false,
				isReplyBoxOpen: false,
				showButtons: false,
				currentArtifactId: null as string | null,
				currentArtifact: null as LightBoxArtifact | null,
				measureDialogVisible: false,
				localUsers: [] as User[],
				localMe: {} as any,
				// cursor: whether mounted()'s user/me load has SETTLED. Without this the "organization unknown"
				// diagnostic fired on every healthy lightbox open, because the gate runs on first render while
				// getMe() is still in flight — a warning that appears every time it works undermines exactly
				// the diagnostic value it was added for.
				localMeLoadSettled: false,
				// ls-heroku-ci: which (artifact, reason) pairs have already been reported. The gate below runs
				// from a template v-if, so it re-evaluates on every render; without this, one withheld banner
				// would fill the console. Instance-scoped, so reopening the lightbox reports afresh.
				//
				// cursor: markRaw, because `data()` would otherwise make this Set REACTIVE — and the gate that
				// reads it runs inside the render effect. `has()` would register a dependency that `add()`
				// immediately invalidates, so every newly-recorded refusal would schedule an extra render of
				// the whole lightbox. Bookkeeping about what has been logged must not be able to drive
				// rendering; it is the wrong kind of state to be reactive at all.
				reportedReminderGateReasons: markRaw(new Set<string>()),
				// Review: the external Rotate/Delete gate gets its OWN dedupe set — sharing the
				// reminder gate's set works only while the reason strings happen to differ.
				reportedExternalGateReasons: markRaw(new Set<string>()),
				// Cursor: single-flight marker per conversation for resolveUploaderIdentity — without
				// it, overlapping lookups (fast gallery navigation into an uncached conversation) can
				// race, and a later FAILURE would cache null over an earlier success, permanently
				// hiding Rotate/Delete from the true uploader for this lightbox instance. markRaw:
				// in-flight bookkeeping must not drive rendering.
				pendingUploaderLookups: markRaw(new Set<string>()),
				showThumbs: false,
				isLandscape: window.matchMedia("(orientation: landscape)").matches,
				previousArtifactIds: [] as string[],
				previousArtifactId: null as string | null,
				allUsersCache: [] as User[],
				forcedUserIdsByConversationId: {} as Record<string, string | null>,
				// LSP-17772 (review): uploader identity per conversation, from the share-extended
				// upload-status endpoint (`conversationCreatedBy`). `null` = looked up and unknown
				// (endpoint failed or conversation has no creator); absent key = not looked up yet.
				uploaderIdsByConversationId: {} as Record<string, string | null>,
				localUsersRequestId: 0,
				snapshotNotesPanel: null,
				tagDialogVisible: false,
				savingTags: false,
				currentArtifactUrl: null as string | null,
				uploadSnapshotErrorMessage: `It looks like something went wrong and your snapshot failed to upload.  Please try again. If you continue to experience this error, contact our support team at ${window.env.VITE_SUPPORT_EMAIL}`,

				// Carousel animations for smooth lightbox opening/closing
				carouselTransition: null as string | null,
				carouselTransitionReverse: null as string | null,
				// LSP-21973: the slide being navigated away from, kept rendered so its exit animation is not blank.
				outgoingArtifactId: null as string | null,
				// LSP-21973: chosen by the lightbox, not VWindow — see the currentArtifactId watcher.
				slideTransition: "v-window-x-transition" as "v-window-x-transition" | "v-window-x-reverse-transition",

				clipDialogVisible: false,
				lastSaveToastId: null as number | string | null,

				// Date editing
				dateEditDialogVisible: false,
				editDatePart: "",
				editTimeValue: "",
				initialEditDateValue: "",
				editOriginalSeconds: 0,
				editOriginalMilliseconds: 0,
				// Reactive minute-tick so `editDateMaxValue` re-evaluates across midnight
				// while the edit-date dialog is open. Bumped every 60s by `editDateNowTimer`.
				editDateNowTick: 0,
				editDateNowTimer: null as ReturnType<typeof setInterval> | null,
				savingDate: false,
				settingThumbnail: false,
				downloadingArtifact: false,
				// Playback recovery collections must not drive rendering. Keep them non-reactive
				// and expose only their membership changes through the narrow version signal.
				playbackRetriedArtifactIds: markRaw(new Set<string>()),
				pendingPlaybackHealUrls: markRaw(new Map<string, string | null>()),
				pendingPlaybackHealVersion: 0,
				// Concurrent refreshes still emit whole-array v-model replacements. Serialize only
				// those writes so each override derives from the prop update emitted before it.
				playbackHealModelWriteQueue: markRaw(Promise.resolve()),
				// Advance on both close and open so queued recovery work can prove it still belongs
				// to the exact lightbox lifecycle session that started it.
				playbackRecoverySessionId: 0,
				// Parent id of the artifact that the most recent annotation was saved against.
				// Captured at save time so the `artifacts` watcher can reconcile the optimistic
				// `temp-annotation-…` id to the real artifact created server-side.
				tempArtifactParentId: null as string | null,
				tempAnnotationObjectUrl: null as string | null,
				retiredTempAnnotationObjectUrls: markRaw(new Set<string>()),
				// Stable for the lifetime of one open annotation edit. A presign timeout can happen
				// after the server creates its pending snapshot but before the browser receives the URL;
				// retrying that same edit must present the same backend dedupe identity.
				annotationUploadIdempotencyId: null as string | null,
				annotationUploadStartedAt: null as number | null,
				// A target response can be lost after the API has created its pending
				// Snapshot row. Retain the whole logical capture so a retry cannot drift
				// to a later playhead while reusing (or abandoning) the pending row.
				snapshotUploadAttempts: markRaw(
					new Map<
						string,
						{
							blob: Blob;
							captureOffsetMs: number;
							metadata: Record<string, string | number>;
							// Bugbot (PR #4816): set once this exact capture's upload is actually dispatched,
							// so a repeat click that hits the dedup branch above (same paused position, or
							// the same rounded capture millisecond) coalesces onto the SAME in-flight request
							// instead of calling api.uploadSnapshot(...) again with the identical id/blob --
							// re-enqueuing an already-"pending" durable row resets its retry bookkeeping and
							// can drive a duplicate presign+PUT of identical bytes mid-flight.
							uploadPromise: Promise<void> | undefined;
						}
					>()
				),
				// Wall-clock instant the current annotation save began. The reconciliation
				// watcher uses this as a lower bound on `createdAt` when picking the real
				// child — without it, a parent with pre-existing older annotations would
				// snap to the WRONG sibling whenever our freshly-saved one has not yet
				// surfaced server-side (race + first-match hazard).
				tempArtifactSaveStartedAt: null as number | null,
				// LSP-16940: the optimistic placeholder (blob-URL edited copy) kept in memory
				// so it can be RE-INJECTED into the refreshed list. Azure search lags up to
				// ~1 min, so a timeline reload wipes the placeholder before the real child is
				// indexed. Rather than snapping back to the parent (original) — which made the
				// user think their edit was lost — we keep showing this placeholder until the
				// real child surfaces, then switch to it.
				tempArtifactPlaceholder: null as IndexableDocument | null
			};
		},
		computed: {
			mediaItems: {
				get() {
					return this.modelValue;
				},
				set(value: IndexableDocument[]) {
					this.$emit("update:modelValue", value);
				}
			},
			artifacts() {
				return this.mediaItems.map(mapResultToLightBoxArtifact) as LightBoxArtifact[];
			},
			// LSP-21973: the carousel's only items. Registering an item with Vuetify's group walks the carousel's
			// existing children, so a 2,000-item session spent 10-20s just registering slides on open. The
			// order is previous → current → next (wrapping like the arrows), so the carousel's own arrows and
			// swipe land on the right neighbour, which is already mounted and preloading; the slide animating
			// out follows them.
			carouselArtifacts(): LightBoxArtifact[] {
				const slides = this.artifacts;
				const currentIndex = slides.findIndex((artifact) => artifact.id === this.currentArtifactId);
				// An id the list does not hold (yet) keeps the old behaviour: with every slide registered,
				// VCarousel's mandatory selection fell back to the first one, so register just that slide.
				if (currentIndex === -1) return slides.slice(0, 1);
				const slideWindow = [
					slides[(currentIndex - 1 + slides.length) % slides.length],
					slides[currentIndex],
					slides[(currentIndex + 1) % slides.length]
				];
				const outgoing = slides.find((artifact) => artifact.id === this.outgoingArtifactId);
				if (outgoing) slideWindow.push(outgoing);
				return [...new Set(slideWindow)];
			},
			currentMediaItem() {
				if (!this.currentArtifact?.id) {
					return null;
				}

				return this.mediaItems.find((item) => (item.searchResultId ?? item.id) === this.currentArtifact?.id) ?? null;
			},
			isDateEditableArtifact() {
				return this.currentMediaItem?.entityType === "snapshot" || this.currentMediaItem?.entityType === "recording";
			},
			isDesktop() {
				return this.display.mdAndUp.value;
			},
			// The desktop toolbar's shared actions (MediaLightboxActions); `null` hides an action.
			desktopLightboxActions() {
				const processingReason = this.isTempAnnotation ? "The image is still processing" : null;
				return {
					tag: this.showTags ? { disabled: this.isTempAnnotation, reason: processingReason } : null,
					rotate: this.showRotate
						? {
								disabled: this.isGifSnapshot || this.isArtifactUpdating || this.isTempAnnotation,
								reason: this.imageRotationUnavailableReason,
								inert: this.isGifSnapshot
							}
						: null,
					annotate: this.showEdit
						? {
								disabled: this.isGifSnapshot || this.isArtifactUpdating || this.isTempAnnotation,
								reason: this.imageAnnotationUnavailableReason,
								inert: this.isGifSnapshot
							}
						: null,
					caption: this.showCaption
						? {
								disabled: this.isGifSnapshot || this.isTempAnnotation,
								reason: this.imageEditingUnavailableReason,
								inert: this.isGifSnapshot
							}
						: null,
					remove: this.showDelete ? { disabled: this.isTempAnnotation, reason: processingReason } : null
				};
			},
			isMobile() {
				return this.display.smAndDown.value;
			},
			// LSP-19030: the lightbox media's height cap is breakpoint-dependent and lives here rather
			// than in ZoomablePhoto or VideoPlayer, so it reads in the same idiom as the media-frame
			// wrapper right above the media (`maxHeight: isMobile ? '100%' : '80%'`) and is expressed
			// exactly once for every media type. Desktop stays uncapped so the media fills the frame's
			// full height (LSP-12175, LSP-17655); small screens get the cap back so tall media cannot
			// overrun the available viewport height and shove the caption, comments and action controls
			// out of reach.
			//
			// It is deliberately NOT named for the snapshot: the first cut of LSP-19030 capped only the
			// `artifactType === 'snapshot'` branch, and a device test found the sibling
			// `artifactType === 'recording'` branch still rendering a video far taller than the photo
			// next to it. Both branches now consume this one value.
			// LSP-19567: bounded by the carousel's guaranteed share of the viewport as well as by the
			// fixed 800px. `display.height` is Vuetify's reactive viewport height, so the cap follows a
			// rotation or a browser-chrome resize instead of being sampled once at open.
			lightboxMediaMaxHeight() {
				if (!this.isMobile) {
					return null;
				}
				return lightboxMobileMediaHeight(this.display.height.value);
			},
			// LSP-21116: the mobile media frame's HEIGHT, and the value that sets it. Every media surface —
			// the photo frame (`.snapshot-container`, which the displayed photo and the eager preload image
			// both fill), the video container and the still-processing placeholder — reads this ONE value, so
			// swiping between a portrait photo, a landscape photo and a video never moves the controls
			// (the LSP-19030 contract), and a px height is known at first paint, so the frame cannot jump
			// when the media finishes loading.
			//
			// It is the cap above on purpose. LSP-19030 derived the height from the frame's WIDTH instead
			// (`aspect-ratio: 16 / 9`), which on a portrait phone made a ~197px frame under a carousel that
			// reserves at least half the screen, so a video sat above an empty band and a portrait video
			// shrank to a strip between side bars. Filling the cap uses exactly the space the carousel
			// guarantees, and on a landscape phone it is the same 50%-of-viewport bound LSP-19567 needs to
			// keep the video control bar in view. Media is contained inside the frame (letterboxed or
			// pillarboxed). Null on desktop, where the frame fills the lightbox (LSP-12175, LSP-17655).
			lightboxMediaFrameHeight() {
				return this.lightboxMediaMaxHeight;
			},
			transition() {
				return this.carouselTransition;
			},
			transitionReverse() {
				return this.carouselTransitionReverse;
			},
			showTags() {
				try {
					if (this.hasFreeTrialEnded) {
						return false;
					}

					const meOrgId = this.localMe?.organization?.id;
					if (!meOrgId) {
						return false;
					}
					if (this.parentType === "checklist") {
						return false;
					}

					// project-level org
					if (this.project && this.project?.organizationId !== meOrgId) return false;

					// artifact-level org
					if (this.currentArtifact && this.currentArtifact.organizationId && this.currentArtifact.organizationId !== meOrgId) {
						return false;
					}
					// master document org
					if (
						this.currentArtifact &&
						this.currentArtifact.MasterDocument &&
						this.currentArtifact.MasterDocument.organizationId &&
						this.currentArtifact.MasterDocument.organizationId !== meOrgId
					) {
						return false;
					}
					// master recording org
					if (
						this.currentArtifact &&
						this.currentArtifact.MasterRecording &&
						this.currentArtifact.MasterRecording.organizationId &&
						this.currentArtifact.MasterRecording.organizationId !== meOrgId
					) {
						return false;
					}
					return this.isAuthenticated && !this.isViewOnly;
				} catch (e) {
					return false;
				}
			},
			isGifSnapshot() {
				// Uploaded GIFs retain their format in the canonical media URL. Ignore signed query parameters.
				return /\.gif$/i.test((this.currentArtifact?.mediaUrl ?? "").split(/[?#]/)[0]);
			},
			imageEditingUnavailableReason() {
				return this.isGifSnapshot ? "GIF files cannot be edited." : this.isTempAnnotation ? "The image is still processing" : "";
			},
			imageRotationUnavailableReason() {
				return this.isGifSnapshot ? "GIF files cannot be rotated." : this.imageEditingUnavailableReason;
			},
			imageAnnotationUnavailableReason() {
				return this.isGifSnapshot ? "GIF files cannot be annotated." : this.imageEditingUnavailableReason;
			},
			showEdit() {
				return (
					!this.hasFreeTrialEnded &&
					this.isAuthenticated &&
					!this.isViewOnly &&
					this.artifactType === "snapshot" &&
					this.parentType !== "checklist"
				);
			},
			canCopyArtifactLink() {
				if (!this.isAuthenticated || this.videoProcessing) return false;

				const account = this.localMe?.account;
				const viewerOrganizationId = this.localMe?.organization?.id;
				const artifactOrganizationId =
					this.project?.organizationId ??
					this.currentArtifact?.MasterDocument?.organizationId ??
					this.currentArtifact?.MasterRecording?.organizationId ??
					this.currentArtifact?.organizationId;

				return Boolean(
					account &&
					viewerOrganizationId &&
					artifactOrganizationId === viewerOrganizationId &&
					new Account(account).hasPermissionTo("READ_CONVERSATION")
				);
			},
			// Media-destructive gates retain the artifact-org fallback for conversation/session
			// mounts that cannot derive project share origin. Project-level writes use the explicit
			// isExternalEditor prop instead, so owners retain controls on collaborator uploads.
			isExternalArtifactContext() {
				if (this.isExternalEditor) return true;
				const meOrgId = this.localMe?.organization?.id;
				if (!meOrgId || !this.currentArtifact) return false;
				const artifactOrgIds = [
					this.currentArtifact.organizationId,
					this.currentArtifact.MasterDocument?.organizationId,
					this.currentArtifact.MasterRecording?.organizationId
				];
				return artifactOrgIds.some((orgId) => !!orgId && orgId !== meOrgId);
			},
			// LSP-17772: mirrors the server's own-uploads-only rule (snapshots.ts
			// assertExternalEditorOwnsUpload) — the uploader is the parent conversation's CREATOR.
			// The identity comes from the share-extended upload-status endpoint, fetched by the
			// currentArtifact watcher below: the index document's User.id is NEVER populated for
			// snapshot-sourced masters (sync_snapshot_to_master syncs no createdBy), so comparing
			// against it hid Rotate/Delete from the true uploader (review high #1). Pure computed —
			// diagnostics live in resolveUploaderIdentity (review medium #6).
			ownsCurrentUpload() {
				const conversationId = this.currentArtifact?.conversationId;
				const uploaderId = conversationId ? this.uploaderIdsByConversationId[conversationId] : undefined;
				const myUserId = this.localMe?.user?.id;
				return !!uploaderId && !!myUserId && uploaderId === myUserId;
			},
			// Rotate replaces the image bytes via a presigned PUT, so an external editor rotating a
			// photo they did NOT upload would overwrite another org's original — the server 403s it
			// (own-uploads-only); hide the button instead of surfacing that 403.
			showRotate() {
				return this.showEdit && (!this.isExternalArtifactContext || this.ownsCurrentUpload);
			},
			canEditDate() {
				return (
					!this.hasFreeTrialEnded &&
					this.isAuthenticated &&
					!this.isViewOnly &&
					!this.isTempAnnotation &&
					this.parentType !== "checklist" &&
					this.isDateEditableArtifact
				);
			},
			showCaption() {
				return (
					!this.hasFreeTrialEnded &&
					this.isAuthenticated &&
					!this.isViewOnly &&
					this.artifactType === "snapshot" &&
					this.parentType !== "checklist"
				);
			},
			showSetProjectThumbnail() {
				return (
					!this.hasFreeTrialEnded &&
					this.isAuthenticated &&
					!this.isViewOnly &&
					!!this.projectId &&
					this.artifactType === "snapshot" &&
					this.parentType !== "checklist" &&
					// LSP-17772 (QA): setting the thumbnail is a PROJECT update — org-scoped
					// server-side, so an external editor's click could only 403/404. Hide it,
					// like every other action the share does not permit.
					this.externalAccessResolved &&
					!this.isExternalEditor
				);
			},
			showDelete() {
				return (
					!this.hasFreeTrialEnded &&
					this.isAuthenticated &&
					!this.isViewOnly &&
					// LSP-19254: recordings used to be exempted from this check entirely, so Delete was offered
					// on a recording that was still being written. Deleting one mid-recording tore down its
					// row while Zoom kept streaming, and since Zoom's Ended event is the only signal any
					// participant has that a recording finished, nobody was told: the person who pressed
					// Stop waited out a 20s client watchdog and every other participant's recording
					// indicator stayed on.
					//
					// Gated on "still being produced" rather than videoProcessing (Cursor Bugbot, verified
					// REAL). videoProcessing is true for EVERY status that is not ready/completed, which
					// also covers the terminal `failed` row — and `failed` is the state a user most needs to
					// clear, with this lightbox the only UI caller of deleteRecording. See
					// isRecordingStillBeingProduced for why `pending` cannot simply join it.
					!this.isRecordingStillBeingProduced &&
					this.parentType !== "checklist" &&
					// LSP-17772: delete is own-uploads-only for external editors (server-enforced in
					// snapshots.ts/recordings.ts) — hide the button instead of surfacing the 403.
					(!this.isExternalArtifactContext || this.ownsCurrentUpload)
				);
			},
			showCommentBox() {
				const showCommentBox = !this.hasFreeTrialEnded && this.isAuthenticated;
				return showCommentBox;
			},
			showControls() {
				const showControls = !this.annotating;
				return showControls;
			},
			artifactDate() {
				return timezones.formatDate(this.currentArtifact?.createdAt);
			},
			editDateValidation() {
				// Touch the minute-tick so future-time errors clear when the wall clock advances,
				// matching `editDateMaxValue`'s reactive tracking — otherwise `:max` updates every
				// minute but the validation message stays stale.
				void this.editDateNowTick;
				return validateMediaEditDate(this.editDatePart, this.editTimeValue);
			},
			editDateValue() {
				return this.editDateValidation.isoValue ?? "";
			},
			isEditDateValid() {
				return this.editDateValidation.isValid;
			},
			editDateError() {
				return this.editDateValidation.error;
			},
			editDatePickerModel: {
				get() {
					if (!this.editDatePart) {
						return null;
					}
					const [year, month, day] = this.editDatePart.split("-").map(Number);
					return new Date(year, month - 1, day);
				},
				set(value: Date | string | null) {
					if (!value) {
						this.editDatePart = "";
						return;
					}
					if (value instanceof Date) {
						this.editDatePart = `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
						return;
					}
					this.editDatePart = value;
				}
			},
			editDateMaxValue() {
				// `new Date()` is not reactive, so a computed with no deps would cache forever.
				// Touch `dateEditDialogVisible` so each dialog open re-evaluates "today", and
				// touch `editDateNowTick` (bumped every minute while the dialog is open) so the
				// max value advances across midnight without requiring the user to reopen.
				//
				// LSP-19247: this deliberately does NOT blank the value while the dialog is closed.
				// It used to `return ""` for that case, which strips the `max` attribute the instant
				// the dialog starts closing — i.e. while it is still on screen fading out. Chrome
				// sizes an `input[type="date"]`'s year sub-field from the min/max RANGE, so dropping
				// the max re-measured the control: the field jumped from 155px to 173px and dragged
				// the calendar glyph right with it, one frame before the dialog disappeared. Both
				// reads below are still tracked, so each open re-evaluates "today" exactly as before;
				// the value simply stays valid instead of momentarily becoming invalid.
				void this.dateEditDialogVisible;
				// Reference the tick so Vue tracks it as a reactive dep of this computed.
				void this.editDateNowTick;
				const today = new Date();
				const year = today.getFullYear();
				const month = String(today.getMonth() + 1).padStart(2, "0");
				const day = String(today.getDate()).padStart(2, "0");
				return `${year}-${month}-${day}`;
			},
			editDateMinValue() {
				return `${MEDIA_DATE_MIN_YEAR}-01-01`;
			},
			hasEditDateChanged() {
				return !!this.editDateValue && this.editDateValue !== this.initialEditDateValue;
			},
			isCommentBoxOpen() {
				return this.commenting || this.isDesktop;
			},
			replyBoxOpen() {
				return this.openReplyBoxIds.size > 0;
			},
			artifactId() {
				return this.currentArtifactId ?? "";
			},
			// Read-only compatibility projection for callers that inspect whether the selected
			// artifact is awaiting recovery. The Map remains the only stored pending-heal state.
			pendingPlaybackHealArtifactId() {
				// The version invalidates this projection exactly when raw Map membership changes.
				void this.pendingPlaybackHealVersion;
				return this.currentArtifactId && this.pendingPlaybackHealUrls.has(this.currentArtifactId) ? this.currentArtifactId : null;
			},
			artifactType() {
				return this.currentArtifact?.type ?? "";
			},
			// LSP-21125: the search document's masterRecordingId, which mapResultToLightBoxArtifact carries as
			// `MasterRecording.id`. The captions read is keyed by it, not by the artifact id.
			currentMasterRecordingId(): string | null {
				if (this.artifactType !== "recording") return null;
				return normalizeIdentifier(this.currentArtifact?.MasterRecording?.id) ?? null;
			},
			currentRecordingCaptions(): RecordingCaptionsResponse | null {
				const masterRecordingId = this.currentMasterRecordingId;
				return masterRecordingId ? (this.recordingCaptionsByMasterRecordingId[masterRecordingId] ?? null) : null;
			},
			// LSP-21125: Live Translations ships dark — `/me` config.featureFlags.live_translations gates the captions
			// read, the toggle and the panel. Read like capture_evidence from the already-loaded `me`, and from
			// `localMe` once mounted() has it, so a lightbox opened before `me` loaded still picks the flag up.
			liveTranslationsEnabled(): boolean {
				return isMeFeatureFlagEnabled(this.localMe, "live_translations") || isLoadedMeFeatureFlagEnabled("live_translations");
			},
			showTranslationToggle(): boolean {
				return this.liveTranslationsEnabled && hasCaptionLines(this.currentRecordingCaptions);
			},
			// Desktop: the panel swaps into the right-hand column in place of Comments. Phone width: it opens under
			// the controls row, and gives way while the mobile Comments overlay is open.
			translationPanelVisible(): boolean {
				return (
					this.translationPanelOpen &&
					this.showTranslationToggle &&
					!this.captureEvidenceExpanded &&
					(this.isDesktop || !this.commenting)
				);
			},
			commentParentType() {
				return this.currentArtifact?.type === "snapshot"
					? "photo"
					: this.currentArtifact?.type === "recording"
						? "recording"
						: "video";
			},
			user() {
				return this.currentArtifact?.User;
			},
			actionClass() {
				if (this.annotating) {
					return "annotating";
				} else {
					return "comment";
				}
			},
			currentArtifactIndex() {
				if (!this.currentArtifactId || !this.artifactList.length) {
					return 0;
				}
				return this.artifactList.findIndex((artifact) => artifact.id === this.currentArtifactId);
			},
			// LSP-19254: one normalization for both this and the delete gate, which duplicated it verbatim.
			//
			// THREE states, not two. "no status field at all" is a legacy document and is allowed to
			// delete; "a status field that is not a string" is drift on a destructive gate and must not
			// be waved through as if the field were missing (ls-heroku-ci). `undefined` is returned only
			// for the first; the second returns null.
			normalizedRecordingStatus() {
				const status = this.currentArtifact?.status;
				if (status === undefined || status === null) {
					return undefined;
				}
				return typeof status === "string" ? status.toUpperCase() : null;
			},
			videoProcessing() {
				if (this.currentArtifact?.type !== "recording") {
					return false;
				}

				// Reads the shared normalization rather than repeating it: `status` is plumbed straight
				// from the search document and is absent (or, on drift, not a string) often enough that a
				// bare `.toUpperCase()` here threw and took the whole lightbox render down — including the
				// incomplete-upload banner beside it (break-it round 3). Playability does not care WHICH
				// unusable shape it got, so absent and malformed fold together here — unlike the delete
				// gate, which distinguishes them.
				const status = this.normalizedRecordingStatus;
				if (status === undefined || status === null) {
					// Unknown status is not evidence of processing; show the video rather than a spinner,
					// matching how shouldShowIncompleteBanner treats an absent status.
					return false;
				}

				return status !== RecordingStatus.Ready.toUpperCase() && status !== RecordingStatus.Completed.toUpperCase();
			},
			// LSP-19254: is this recording actively being produced right now? Distinct from
			// videoProcessing, which answers "can we play it yet" and is therefore true for terminal
			// failures too. Returns the DECISION PLUS ITS REASON so the gate is not silent — see the
			// watcher below, which logs it. A bare boolean here left five branches with no diagnostic at
			// all, which the repo's silent-failure rule does not allow for a gate this destructive.
			//
			// `MasterRecordings.recordingStatus` — what the search document carries — is written by TWO DB
			// triggers, and both matter here:
			//   - from `RecordingSession.jobStatus` (async sessions): pending / processing / failed /
			//     completed. Never `inprogress`.
			//   - from `RecordingChannel` (20260605120000-channel-sync-trigger-upsert): `inprogress`
			//     while isIvsRecordingStarted && !isIvsRecordingStopped && !isIvsRecordingCompleted.
			// That second one is a LIVE call recording, which is exactly this ticket's scenario, so the
			// in-progress branch is load-bearing and not dead code. A review read only the first trigger
			// and concluded otherwise; the enumeration is here so the next reader does not repeat that.
			deleteGateDecision() {
				// `entityType ?? type`, the predicate helpers/incompleteRecordingUpload.ts documents: the
				// mapper derives `type` FROM `entityType`, and a destructive gate should not be the one
				// place that reads only the derived half (ls-heroku-ci, low).
				if ((this.currentArtifact?.entityType ?? this.currentArtifact?.type) !== "recording") {
					return { withheld: false, reason: "not-a-recording" };
				}
				const status = this.normalizedRecordingStatus;
				if (status === undefined) {
					// No status field at all: a legacy document. Not evidence a recording is being written,
					// and hiding the control on every one of these would strand them — so the pre-existing
					// behaviour is kept, matching isIncompleteRecordingUpload.
					return { withheld: false, reason: "status-absent" };
				}
				if (status === null) {
					// A status field that is not a string. That is drift, not a legacy shape, and it belongs
					// with the unrecognized-value case below rather than with the absent one — a recording
					// still being written whose status arrived as a number would otherwise be handed back
					// its Delete button (ls-heroku-ci, high).
					return { withheld: true, reason: "status-not-a-string" };
				}
				if (status === RecordingStatus.InProgress || status === "IN_PROGRESS" || status === RecordingStatus.Processing) {
					// "IN_PROGRESS" is not in the enum on purpose: the DB enum spells it `inprogress`, while
					// RecordingChannel.getRecordingStatus() returns `in_progress`. Both are accepted rather
					// than betting on which reaches this component.
					return { withheld: true, reason: `still-being-produced:${status}` };
				}
				if (status === RecordingStatus.Ready || status === RecordingStatus.Completed || status === RecordingStatus.Failed) {
					// READY / COMPLETED / FAILED are terminal. FAILED in particular MUST stay deletable: it
					// is the state a user most needs to clear, and this lightbox is the only UI caller of
					// deleteRecording. Spelled as an allowlist rather than `!== Pending`, which let ANY
					// unrecognized value fall through to "safe to delete" — see the closing branch.
					return { withheld: false, reason: `terminal:${status}` };
				}
				// PENDING is ambiguous, and treating it as terminal reopened this very bug: `status` is
				// written from jobStatus alone, so every session that has not reached MediaConvert reads
				// 'pending' — per controllers/masterRecordings.ts, "an upload streaming right now and an
				// upload that died an hour ago are indistinguishable". Only a positive server verdict
				// releases the control; anything else fails CLOSED, because wrongly allowing a mid-write
				// delete recreates the orphaned stream this ticket is about. Strict `=== true` for the same
				// reason helpers/incompleteRecordingUpload.ts uses it.
				if (status === RecordingStatus.Pending) {
					// The two upload verdicts are ASYNC-ONLY. `IndexService.hydrateRecordings` stamps
					// `isUploadComplete = true` and `isUploadAbandoned = false` on every non-async recording
					// ("assume that all recordings that are not async are ready to view") and only overwrites
					// them from RecordingSession when recordingType === "async". A live/ark recording sits in
					// `pending` during the window before the channel-sync trigger flips it to `inprogress`, so
					// reading that hardcoded `true` would hand back Delete during a live capture — this
					// ticket's exact bug, through a second door (ls-heroku-ci review, verified against
					// IndexService).
					if (this.currentArtifact?.recordingType !== RecordingType.ASYNC) {
						return { withheld: true, reason: "pending-live-recording-has-no-upload-verdict" };
					}
					if (this.currentArtifact?.isUploadAbandoned === true) {
						return { withheld: false, reason: "pending-upload-abandoned" };
					}
					if (this.currentArtifact?.isUploadComplete === true) {
						return { withheld: false, reason: "pending-upload-complete" };
					}
					return { withheld: true, reason: "pending-upload-state-unknown" };
				}
				// A status this component does not know. It is drift, not data: the terminal set
				// (READY/COMPLETED/FAILED) is complete, so a status added upstream is far likelier to be a
				// new in-flight state than a new finished one. Fails CLOSED for the same reason PENDING
				// does — wrongly allowing a mid-write delete recreates the orphaned stream this ticket is
				// about, while wrongly withholding is merely inconvenient — and is logged so the drift is
				// visible instead of silently widening the destructive path.
				return { withheld: true, reason: `unrecognized-status:${status}` };
			},
			// LSP-19254: artifact identity AND decision, which is exactly what the log needs to be keyed on.
			// Keyed on the reason alone, paging between two recordings in the same bucket logged only the
			// first; keyed on the decision OBJECT, a list refresh re-logged the same artifact and decision
			// because the computed returns a fresh object per evaluation. Both were review findings.
			deleteGateLogKey() {
				// The artifact's OWN id, not currentArtifactId: the log names currentArtifact.id, and the two
				// are set independently — keying on the other one meant a gallery step that swapped the
				// artifact without the id going unlogged.
				return `${this.currentArtifact?.id}:${this.deleteGateDecision.reason}`;
			},
			isRecordingStillBeingProduced() {
				return this.deleteGateDecision.withheld;
			},
			isArtifactUpdating() {
				return !!this.currentArtifact?.updating;
			},
			isTempAnnotation() {
				return !!this.currentArtifactId?.startsWith("temp-annotation-");
			},
			showClipVideoButton() {
				return !this.videoProcessing && this.artifactType === "recording" && !this.isViewOnly;
			},
			measureSourceKey(): string {
				return `${this.artifactType}:${this.currentArtifact?.id ?? ""}:${this.currentArtifact?.conversationId ?? ""}`;
			},
			showMeasureButton(): boolean {
				return this.captureMeasureUiEnabled && this.artifactType === "snapshot" && this.measureAvailable && !this.annotating;
			}
		},
		watch: {
			// LSP-21125: the currentArtifact watcher below is immediate, so it runs before mounted() loads
			// `localMe`; when that load turns the flag on, read the captions for the recording already on screen.
			liveTranslationsEnabled(enabled: boolean) {
				if (enabled) void this.loadCurrentRecordingCaptions();
			},
			// LSP-21125: time updates are ignored while the panel is hidden, so when it appears (toggle, or
			// navigating onto a recording with lines) start it from where the player actually is.
			translationPanelVisible(isVisible: boolean) {
				if (!isVisible) return;
				const playerSeconds = Number(this.currentVideoPlayer()?.currentTime);
				this.recordingPlaybackSeconds = Number.isFinite(playerSeconds) ? playerSeconds : 0;
			},
			// LSP-19254: diagnostic logging for the delete gate, per CLAUDE.md → "Diagnostic logging when
			// fixing silent failures". The gate has five fail-conditions and had none, so a future "why
			// could/couldn't I delete this" report had nothing to read.
			//
			// Logged from a watcher rather than inside the computed on purpose: the computed re-evaluates
			// on every render, so logging there would flood the console with the same line. Watching the
			// DECISION rather than its `reason` string: the reason carries no artifact identity, so paging
			// through a gallery of recordings that all land in the same bucket ("terminal:COMPLETED") never
			// changed the watched value and every artifact after the first went unlogged — which is the
			// correlation the log exists to provide (ls-heroku-ci review). The computed returns a fresh
			// object per evaluation, so this fires once per artifact-or-decision change.
			//
			// BOTH sides are logged. Logging only the withheld side left every ALLOW path — including the
			// absent-status one, which permits deleting a recording whose state we could not read at all —
			// with no diagnostic trail, which is the same blind spot on the opposite side.
			deleteGateLogKey: {
				immediate: true,
				handler() {
					const decision = this.deleteGateDecision;
					if ((this.currentArtifact?.entityType ?? this.currentArtifact?.type) !== "recording") {
						return;
					}
					const withheld = !!decision?.withheld;
					const fields = {
						reason: decision?.reason,
						recordingId: this.currentArtifact?.id,
						conversationId: this.currentArtifact?.conversationId,
						status: this.currentArtifact?.status,
						isUploadComplete: this.currentArtifact?.isUploadComplete,
						isUploadAbandoned: this.currentArtifact?.isUploadAbandoned
					};
					if (withheld) {
						console.warn("MediaLightBox: withholding Delete for a recording that may still be written", fields);
						return;
					}
					console.info("MediaLightBox: allowing Delete for a recording", fields);
				}
			},
			// LSP-17772 (review): the uploader identity behind ownsCurrentUpload is fetched per
			// conversation, only in external contexts. `immediate` covers the artifact the
			// lightbox opens on; the watcher covers gallery navigation.
			currentArtifact: {
				handler() {
					void this.resolveUploaderIdentity();
					void this.loadCurrentRecordingCaptions();
				},
				immediate: true
			},
			savingAnnotation(isSavingAnnotation: boolean) {
				if (!isSavingAnnotation) {
					this.annotationSavePendingFeedbackShown = false;
				}
			},
			dateEditDialogVisible: {
				// Without this tick, `editDateMaxValue` stays frozen at dialog-open time —
				// a user who keeps the dialog open across midnight still cannot pick "today".
				// `globalThis.setInterval` (vs window.setInterval) keeps jsdom/happy-dom tests tolerant.
				handler(isOpen: boolean) {
					if (isOpen) {
						if (this.editDateNowTimer == null) {
							this.editDateNowTimer = globalThis.setInterval(() => {
								this.editDateNowTick++;
							}, 60_000);
						}
					} else if (this.editDateNowTimer != null) {
						globalThis.clearInterval(this.editDateNowTimer);
						this.editDateNowTimer = null;
					}
				}
			},
			artifacts: {
				async handler(newVal) {
					this.artifactList = newVal;
					// LSP-16827: fresh media arrived after a Refresh tap — stop the spinner.
					this._stopRefreshFeedback();
					if (this.currentArtifactId) {
						const updatedArtifact = this.artifactList.find((x) => x.id === this.currentArtifactId);
						if (updatedArtifact) {
							const previousArtifactId = this.currentArtifact?.id;
							const previousConversationId = this.currentArtifact?.conversationId;
							const replacedObjectUrl =
								this.currentArtifactUrl === this.tempAnnotationObjectUrl &&
								updatedArtifact.mediaUrl !== this.tempAnnotationObjectUrl
									? this.tempAnnotationObjectUrl
									: null;
							this.currentArtifact = updatedArtifact;
							this.currentArtifactUrl = updatedArtifact.mediaUrl;
							if (replacedObjectUrl) {
								this.retireTempAnnotationObjectUrl(replacedObjectUrl);
								this.tempArtifactParentId = null;
								this.tempArtifactSaveStartedAt = null;
							}
							if (updatedArtifact.id !== previousArtifactId || updatedArtifact.conversationId !== previousConversationId) {
								await this.updateLocalUsersForArtifact(this.currentArtifactId);
							}
						} else if (this.tempArtifactParentId && this.currentArtifactId.startsWith("temp-annotation-")) {
							// Optimistic annotation id outlived the optimistic list. The real
							// artifact is created server-side; match it by parentId AND require
							// `createdAt` to be >= the moment this save started, so pre-existing
							// older annotations of the same parent cannot be mistakenly snapped
							// to when the freshly-saved one has not yet surfaced. Within candidates
							// that pass the timestamp gate, pick the newest (id tiebreak defends
							// against equal `createdAt` values).
							const tempParentId = this.tempArtifactParentId;
							const saveStartedAt = this.tempArtifactSaveStartedAt ?? 0;
							const siblings = this.artifactList.filter((entry) => {
								if (entry.parentId !== tempParentId || entry.id.startsWith("temp-")) {
									return false;
								}
								const createdMs = entry.createdAt ? Date.parse(entry.createdAt) : NaN;
								return Number.isFinite(createdMs) && createdMs >= saveStartedAt;
							});
							const realChild = siblings.sort((a, b) => {
								const byTime = (b.createdAt ?? "").localeCompare(a.createdAt ?? "");
								return byTime !== 0 ? byTime : b.id.localeCompare(a.id);
							})[0];
							if (realChild) {
								// The real indexed child finally surfaced — switch to it, retire the
								// optimistic Blob URL (deferred revocation, LSP-18159), and clear the
								// temp reconciliation state. Logged so Datadog correlation is two-sided
								// (every failure branch here logs; the resolution should too).
								logInfo("Temp annotation reconciled to the real indexed child", {
									method: "artifacts watcher",
									file: "MediaLightBox.vue",
									tempId: this.currentArtifactId,
									parentId: tempParentId,
									realChildId: realChild.id
								});
								this.setCurrentArtifactId(realChild.id);
								this.retireTempAnnotationObjectUrl(this.tempAnnotationObjectUrl);
								this.clearTempAnnotationState();
							} else {
								// LSP-16940: the temp id is no longer in the list, so a timeline
								// reload wiped the optimistic placeholder before the freshly-saved
								// child was Azure-indexed (~1 min lag). Previously we snapped back
								// to the PARENT (original) here, which made users think their edit
								// was lost. Instead we re-inject the edited copy so it stays on
								// screen, keeping the reconciliation state so a later pass can swap
								// to the real child once it indexes. Only give up if the parent is
								// gone too (e.g. deleted mid-save).
								const parentStillPresent = this.artifactList.some((entry) => entry.id === tempParentId);
								const reconcileMeta = {
									method: "artifacts watcher",
									file: "MediaLightBox.vue",
									tempId: this.currentArtifactId,
									parentId: tempParentId,
									realChildFound: false,
									parentStillPresent,
									hasStoredPlaceholder: !!this.tempArtifactPlaceholder,
									listSize: this.artifactList.length
								};

								if (this._closeCleanupTimer != null) {
									// The lightbox is CLOSING (the 300ms cleanup timer is armed): the parent just
									// spliced the temp placeholder out of the timeline on close, and that splice is
									// what fired this watcher. Re-injecting here would put a revoked-blob document
									// straight back into the timeline — the exact broken-thumbnail symptom the
									// close-path removal exists to prevent (Bugbot on #2591). Skip; the cleanup
									// timer clears the reconciliation state and object URLs momentarily.
									logInfo("Skipping placeholder re-injection during lightbox close", reconcileMeta);
								} else if (this.tempArtifactPlaceholder && parentStillPresent) {
									this.insertPlaceholderAfterParent(this.tempArtifactPlaceholder, tempParentId);
									logInfo("Re-injecting edited copy while real child is still indexing", reconcileMeta);
								} else {
									// Nothing safe to show — close rather than stranding on a
									// non-existent id; retire the Blob URL and clear the temp state.
									logInfo("Temp annotation could not be reconciled; closing lightbox", reconcileMeta);
									this.$emit("close-lightbox");
									this.retireTempAnnotationObjectUrl(this.tempAnnotationObjectUrl);
									this.clearTempAnnotationState();
								}
							}
						} else if (this.currentArtifactId?.startsWith("temp-annotation-")) {
							// The reconcile gate did NOT fire for a temp-looking id — tempArtifactParentId is null
							// (e.g. a failure path cleared it while currentArtifactId still pointed at the temp id).
							// Without this breadcrumb that state is a silent dead-end (ls-heroku on #2591, HIGH):
							// every failure branch INSIDE the gate logs, but the gate itself fell through mutely.
							logInfo("Temp annotation id present but reconcile gate did not fire", {
								method: "artifacts watcher",
								file: "MediaLightBox.vue",
								currentArtifactId: this.currentArtifactId,
								tempArtifactParentId: this.tempArtifactParentId,
								hasStoredPlaceholder: !!this.tempArtifactPlaceholder,
								listSize: this.artifactList.length
							});
						}
					}
				},
				deep: true,
				immediate: true
			},
			isLightboxThumbsOpen: {
				handler(newVal) {
					this.$emit("update:isLightboxThumbsOpen", newVal);
				}
			},
			async currentArtifactUrl(newUrl: string | null) {
				const artifactId = this.currentArtifact?.id;
				const recoverySessionId = this.playbackRecoverySessionId;
				if (!artifactId) {
					return;
				}
				if (!this.pendingPlaybackHealUrls.has(artifactId)) {
					return;
				}
				const failedUrl = this.pendingPlaybackHealUrls.get(artifactId);
				const normalizedNewUrl = typeof newUrl === "string" ? newUrl.trim() : newUrl;
				if (!normalizedNewUrl) {
					logError(
						"Playback recovery skipped because the refreshed artifact URL is empty",
						new Error("Empty playback recovery URL"),
						{
							method: "currentArtifactUrl watcher",
							file: "MediaLightBox.vue",
							artifactId,
							failedUrl,
							newUrl
						}
					);
					return;
				}
				const normalizedFailedUrl = typeof failedUrl === "string" ? failedUrl.trim() : failedUrl;
				if (normalizedNewUrl === normalizedFailedUrl) {
					return;
				}

				this.pendingPlaybackHealUrls.delete(artifactId);
				this.pendingPlaybackHealVersion += 1;
				await this.$nextTick();
				if (this.currentArtifact?.id !== artifactId || this.playbackRecoverySessionId !== recoverySessionId) {
					logInfo("Playback recovery heal was consumed but reload was skipped because the viewed artifact changed", {
						method: "currentArtifactUrl watcher",
						file: "MediaLightBox.vue",
						artifactId,
						viewedArtifactId: this.currentArtifact?.id,
						recoverySessionId,
						currentRecoverySessionId: this.playbackRecoverySessionId,
						failedUrl,
						newUrl
					});
					return;
				}
				try {
					const videoPlayerRef = this.$refs.videoPlayer as unknown;
					const videoPlayer = Array.isArray(videoPlayerRef) ? videoPlayerRef[0] : videoPlayerRef;
					if (!videoPlayer) {
						logError(
							"Playback recovery could not trigger reload because the video player ref is unavailable",
							new Error("Missing video player ref"),
							{
								method: "currentArtifactUrl watcher",
								file: "MediaLightBox.vue",
								artifactId,
								failedUrl,
								newUrl
							}
						);
					} else if (typeof (videoPlayer as { reloadAndPlay?: unknown }).reloadAndPlay !== "function") {
						logError(
							"Playback recovery could not trigger reload because the video player ref does not expose reloadAndPlay",
							new Error("Missing video player reloadAndPlay function"),
							{
								method: "currentArtifactUrl watcher",
								file: "MediaLightBox.vue",
								artifactId,
								failedUrl,
								newUrl
							}
						);
					}
					(videoPlayer as { reloadAndPlay?: () => void } | undefined)?.reloadAndPlay?.();
				} catch (error) {
					logError("Playback recovery reloadAndPlay failed synchronously", error, {
						method: "currentArtifactUrl watcher",
						file: "MediaLightBox.vue",
						artifactId,
						failedUrl,
						newUrl
					});
				}
			},
			currentArtifactId: {
				// Exit picture-in-picture whenever the current artifact id changes
				handler(newVal, oldVal) {
					this.outgoingArtifactId = oldVal ?? null;
					// LSP-21973: VWindow derives slide direction from item indices, which shift as the
					// carousel's few items come and go, so the lightbox picks it from the full list instead.
					// Previous/next (including the wrap-around) follow the arrows; any other jump follows list
					// order.
					const slides = this.artifacts;
					const oldIndex = slides.findIndex((artifact) => artifact.id === oldVal);
					const newIndex = slides.findIndex((artifact) => artifact.id === newVal);
					if (oldIndex !== -1 && newIndex !== -1) {
						const isPrevious = slides.length > 2 && newIndex === (oldIndex - 1 + slides.length) % slides.length;
						const isNext = slides.length > 2 && newIndex === (oldIndex + 1) % slides.length;
						const isReverse = isPrevious || (!isNext && newIndex < oldIndex);
						this.slideTransition = isReverse ? "v-window-x-reverse-transition" : "v-window-x-transition";
					}
					// LSP-21125: the next recording's player starts at 0 and reports from there.
					this.recordingPlaybackSeconds = 0;
					if (document.pictureInPictureElement) {
						const pipVideo = document.pictureInPictureElement;
						if (pipVideo && !pipVideo.paused) {
							pipVideo.pause();
						}
						document.exitPictureInPicture();
					}
					void this.updateLocalUsersForArtifact(newVal);
				}
			},
			// Tap-to-measure: resolve whether the photo on screen can be measured (it needs capture
			// evidence), which is what shows the Measure button. A new photo also starts a new
			// session list and closes the Measure page.
			measureSourceKey: {
				immediate: true,
				handler() {
					this.measureDialogVisible = false;
					if (!this.captureMeasureUiEnabled || this.artifactType !== "snapshot") {
						this.snapshotMeasure.reset();
						return;
					}
					void this.snapshotMeasure.load(this.currentArtifact?.id, this.currentArtifact?.conversationId);
				}
			}
		},
		async mounted() {
			document.documentElement.style.overscrollBehaviorY = "none";
			document.body.style.overscrollBehaviorY = "none";
			this.isAuthenticated = await (this.$root as any).$auth.isAuthenticated();
			this._orientationQuery = window.matchMedia("(orientation: landscape)");
			this._onOrientationChange = (event: MediaQueryListEvent) => {
				this.isLandscape = event.matches;
				if (this.$refs.lightbox) {
					(this.$refs.lightbox as any).showThumbs = false;
				}
			};
			this._orientationQuery.addEventListener("change", this._onOrientationChange);
			// LSP-16267: capture phase so this listener runs BEFORE child dialogs' bubble-phase
			// @keydown.esc handlers (Dialog.vue's tag/clip/date dialogs synchronously clear
			// their visibility flags on Escape; without capture phase we'd read the flags
			// after they're already false and incorrectly close the lightbox).
			window.addEventListener("keydown", this.handleKeydown, true);
			if (this.isDesktop) {
				window.addEventListener("mousemove", this.handleMouseMove);
			}
			this.isTouch = matchMedia("(hover: none), (pointer: coarse)").matches;
			this.artifactList = this.artifacts;

			if (this.isAuthenticated) {
				const usersPromise = async () => {
					await this.updateLocalUsersForArtifact(this.currentArtifactId);
				};

				const mePromise = async () => {
					if (!this.me) {
						this.localMe = await api.getMe();
					} else {
						this.localMe = this.me;
					}
				};
				try {
					await Promise.all([usersPromise(), mePromise()]);
				} catch (error) {
					console.error("Error getting users or me", error);
				} finally {
					// Settled either way: a rejection is exactly when the Send Reminder gate's "organization
					// unknown" diagnostic becomes meaningful rather than premature.
					this.localMeLoadSettled = true;
					// The immediate currentArtifact watcher fired before localMe existed and bailed;
					// re-resolve now that the signed-in identity (and org, for the external-context
					// check) is known.
					void this.resolveUploaderIdentity();
				}
			}
		},
		created() {
			this._closeCleanupTimer = null as ReturnType<typeof setTimeout> | null;
			// LSP-16827: fallback timer that clears the Refresh spinner if the media list
			// comes back unchanged (video still processing → `artifacts` watcher never fires).
			this._refreshFeedbackTimer = null as ReturnType<typeof setTimeout> | null;
			this._orientationQuery = null as MediaQueryList | null;
			this._onOrientationChange = null as ((event: MediaQueryListEvent) => void) | null;
			this._shutterAudio = null as HTMLAudioElement | null;
			this._snapshotInProgress = false;
		},
		beforeUnmount() {
			this.snapshotMeasure.reset();
			// Invalidate in-flight playback-recovery continuations during teardown, not just explicit close.
			this.playbackRecoverySessionId += 1;
			this.playbackRetriedArtifactIds.clear();
			this.pendingPlaybackHealUrls.clear();
			this.pendingPlaybackHealVersion += 1;
			if (this._closeCleanupTimer) {
				clearTimeout(this._closeCleanupTimer);
				this._closeCleanupTimer = null;
			}
			this._stopRefreshFeedback();
			if (this._orientationQuery && this._onOrientationChange) {
				this._orientationQuery.removeEventListener("change", this._onOrientationChange);
				this._orientationQuery = null;
				this._onOrientationChange = null;
			}
			this._stopShutterAudio();
			this.pendingRemoteArtifactRemovalIds.clear();
			this.releaseAllTempAnnotationObjectUrls();
			if (this.editDateNowTimer != null) {
				globalThis.clearInterval(this.editDateNowTimer);
				this.editDateNowTimer = null;
			}
			document.documentElement.style.overscrollBehaviorY = "auto";
			document.body.style.overscrollBehaviorY = "auto";
			window.removeEventListener("keydown", this.handleKeydown, true);
		},
		emits: {
			"update:modelValue": (_mediaItems: IndexableDocument[]) => true,
			"close-lightbox": () => true,
			"delete-artifacts": (_artifactIds: string[], _callback: () => void) => true,
			"load-recordings": () => true,
			"clip-saved": (_payload: { jobId: string; conversationId?: string }) => true,
			"date-updated": () => true,
			"annotation-saved": () => true
		},
		methods: {
			retireTempAnnotationObjectUrl(expectedObjectUrl: string | null) {
				if (!expectedObjectUrl) return;
				this.retiredTempAnnotationObjectUrls.add(expectedObjectUrl);
				if (this.tempAnnotationObjectUrl === expectedObjectUrl) {
					this.tempAnnotationObjectUrl = null;
				}
			},
			releaseTempAnnotationObjectUrl(expectedObjectUrl = this.tempAnnotationObjectUrl) {
				if (!expectedObjectUrl) {
					return;
				}

				URL.revokeObjectURL(expectedObjectUrl);
				this.retiredTempAnnotationObjectUrls.delete(expectedObjectUrl);
				if (this.tempAnnotationObjectUrl === expectedObjectUrl) {
					this.tempAnnotationObjectUrl = null;
				}
			},
			releaseAllTempAnnotationObjectUrls() {
				const durableMediaObjectUrls = new Set<string>();
				for (const mediaItem of this.mediaItems) {
					if (mediaItem.id.startsWith("temp-annotation-")) continue;
					if (mediaItem.mediaUrl?.startsWith("blob:")) durableMediaObjectUrls.add(mediaItem.mediaUrl);
					if (mediaItem.thumbnailUrl?.startsWith("blob:")) durableMediaObjectUrls.add(mediaItem.thumbnailUrl);
				}
				const objectUrls = new Set(this.retiredTempAnnotationObjectUrls);
				if (this.tempAnnotationObjectUrl) objectUrls.add(this.tempAnnotationObjectUrl);
				for (const objectUrl of objectUrls) {
					if (!durableMediaObjectUrls.has(objectUrl)) this.releaseTempAnnotationObjectUrl(objectUrl);
				}
			},
			releaseUnreferencedRetiredTempAnnotationObjectUrls() {
				const referencedObjectUrls = new Set<string>();
				if (this.currentArtifactUrl?.startsWith("blob:")) referencedObjectUrls.add(this.currentArtifactUrl);
				for (const mediaItem of this.mediaItems) {
					if (mediaItem.mediaUrl?.startsWith("blob:")) referencedObjectUrls.add(mediaItem.mediaUrl);
					if (mediaItem.thumbnailUrl?.startsWith("blob:")) referencedObjectUrls.add(mediaItem.thumbnailUrl);
				}
				for (const objectUrl of [...this.retiredTempAnnotationObjectUrls]) {
					if (!referencedObjectUrls.has(objectUrl)) this.releaseTempAnnotationObjectUrl(objectUrl);
				}
			},
			async setAsProjectThumbnail() {
				if (!this.projectId || !this.currentArtifactId) return;
				this.settingThumbnail = true;
				try {
					await api.updateProjectThumbnail(this.projectId, this.currentArtifactId);
					this.toast.success("Project thumbnail updated!");
				} catch (e) {
					console.error("Error setting project thumbnail:", e);
					this.toast.error(couldNot("update the thumbnail"));
				} finally {
					this.settingThumbnail = false;
				}
			},
			getFilteredUsersByRoleAndAssignee(users: User[], forcedUserId: string | null) {
				const filteredUsers = users.filter((user) => {
					const roleName = user?.Account?.Roles?.[0]?.name;
					return (
						(forcedUserId && user?.id === forcedUserId) ||
						isRoleOrInRoles(roleName, [Role.Administrator, Role.SuperAdministrator])
					);
				});
				return filteredUsers.length > 0 ? filteredUsers : users;
			},
			async getForcedUserIdFromConversation(conversationId?: string) {
				if (!conversationId) {
					return null;
				}

				if (Object.prototype.hasOwnProperty.call(this.forcedUserIdsByConversationId, conversationId)) {
					return this.forcedUserIdsByConversationId[conversationId];
				}

				try {
					const conversation = await api.getConversation(conversationId);
					const forcedUserId = conversation?.forcedUserId ?? null;
					this.forcedUserIdsByConversationId[conversationId] = forcedUserId;
					return forcedUserId;
				} catch (e) {
					console.error("Error fetching conversation for user filtering", e);
					this.forcedUserIdsByConversationId[conversationId] = null;
					return null;
				}
			},
			async updateLocalUsersForArtifact(artifactId: string | null) {
				if (!this.isAuthenticated) {
					return;
				}

				if (this.users.length > 0) {
					this.localUsers = this.users;
					return;
				}

				if (this.projectId || this.project?.id) {
					// For project media, CommentBox handles projectShareUsers via its own
					// getProjectShares call. Set localUsers to empty so only project
					// members appear in the tag bubble.
					this.localUsers = [];
					return;
				}

				const requestId = ++this.localUsersRequestId;

				if (this.allUsersCache.length === 0) {
					try {
						const localUsersResponse = await api.getUsers({ pageSize: 1000 });
						if (requestId !== this.localUsersRequestId) {
							return;
						}
						this.allUsersCache = localUsersResponse.rows || [];
					} catch (e) {
						console.error("Error fetching users for user filtering", e);
						return;
					}
				}

				const artifact = artifactId ? this.artifactList.find((x) => x.id === artifactId) : this.currentArtifact;
				const forcedUserId = await this.getForcedUserIdFromConversation(artifact?.conversationId);
				if (requestId !== this.localUsersRequestId) {
					return;
				}

				this.localUsers = this.getFilteredUsersByRoleAndAssignee(this.allUsersCache, forcedUserId);
			},
			overrideMediaItemFields(artifactId: string, overrides: Partial<IndexableDocument>) {
				this.mediaItems = this.mediaItems.map((document) =>
					(document.searchResultId ?? document.id) === artifactId ? { ...document, ...overrides } : document
				);
			},
			async handleRotate() {
				if (this.isGifSnapshot) return;

				const artifactId = this.currentArtifactId;
				if (!artifactId) {
					return;
				}

				const document = this.mediaItems.find((d) => (d.searchResultId ?? d.id) === artifactId);
				if (document?.entityType !== "snapshot") {
					return;
				}

				const { mediaUrl: originalMediaUrl } = document;
				const finalOverrides: Partial<IndexableDocument> = {};
				this.overrideMediaItemFields(artifactId, { updating: true });
				try {
					const imageResponse = await fetch(originalMediaUrl);
					if (!imageResponse.ok) {
						throw new Error(`Failed to fetch image: ${imageResponse.status} ${imageResponse.statusText}`);
					}
					const imageBlob = await imageResponse.blob();
					const rotatedBlob = await SnapshotHelper.rotateBlob(imageBlob);
					if (!document.conversationId) {
						throw new Error("Cannot rotate a snapshot without its conversation identity");
					}
					const { mediaUrl } = await api.updateSnapshotImage(artifactId, document.conversationId, rotatedBlob);
					let refreshedMediaUrl = mediaUrl;
					if (!refreshedMediaUrl) {
						this.releaseUnreferencedRetiredTempAnnotationObjectUrls();
						refreshedMediaUrl = URL.createObjectURL(rotatedBlob);
						this.retireTempAnnotationObjectUrl(this.tempAnnotationObjectUrl);
						this.tempAnnotationObjectUrl = refreshedMediaUrl;
					}
					finalOverrides.mediaUrl = refreshedMediaUrl;
					finalOverrides.thumbnailUrl = refreshedMediaUrl;
				} catch (error: unknown) {
					// P-4 active-target uniqueness rejects a concurrent replacement before the
					// presigned upload begins. Keep this editor open and retryable: the other
					// edit remains authoritative until it finishes or is explicitly discarded.
					const replacementInProgress =
						typeof error === "object" && error !== null && (error as { error?: unknown }).error === "REPLACEMENT_IN_PROGRESS";
					// LSP-19929: the upload lane releases the dialog once the server has acknowledged every byte but
					// is still finalizing past the waiter budget. That is not a failure: the durable row keeps
					// re-reading the receipt in the background and the rotation lands on its own, so do not tell
					// the user it failed (the old copy was exactly the false toast the ticket fixed).
					const stillFinalizing =
						typeof error === "object" &&
						error !== null &&
						(error as { code?: unknown }).code === "UPLOAD_RECEIPT_PENDING_TIMEOUT";
					const logContext = {
						artifactId,
						conversationId: document.conversationId,
						errorName: error instanceof Error ? error.name : undefined
					};
					if (stillFinalizing || replacementInProgress) {
						logInfo("Snapshot rotation is still pending", { ...logContext, stillFinalizing, replacementInProgress });
					} else {
						logError("Failed to rotate snapshot", redactedUploadFailure(error), logContext);
					}
					if (stillFinalizing) {
						const message =
							"Your rotated snapshot is still saving. Refresh in a moment to see it once the server finishes processing.";
						this.toast.info(message, { timeout: calculateTimeout(message) });
					} else {
						const message = replacementInProgress
							? "Your previous edit is still saving. Please wait for it to finish before trying again."
							: "Failed to rotate snapshot.";
						this.toast.error(message, { timeout: calculateTimeout(message) });
					}
				} finally {
					finalOverrides.updating = false;
					this.overrideMediaItemFields(artifactId, finalOverrides);
				}
			},
			// The v-track directive's payload (directives/track.ts), for the desktop actions that cannot carry it.
			// Runs in the action's own callback, unlike the directive's separate listener, so a throwing analytics
			// provider must not stop the action that follows it.
			trackDesktopLightboxAction(name: string, testId: string) {
				const event = `${name}-clicked`;
				try {
					Logging.log({ name: event, data: { action: "click", testId, node: "button" } });
				} catch (error) {
					console.warn("Lightbox action analytics failed; running the action anyway", { event, error });
				}
			},
			openTagDialog() {
				this.tagDialogVisible = true;
			},
			async loadRecordings() {
				this.$emit("load-recordings");
			},
			async handlePlaybackError(payload?: unknown) {
				const recoverySessionId = this.playbackRecoverySessionId;
				const artifactId = this.currentArtifact?.id;
				if (!artifactId) {
					logInfo("Playback error ignored because no current artifact is selected", {
						method: "handlePlaybackError",
						file: "MediaLightBox.vue",
						payload
					});
					return;
				}
				if (typeof payload !== "object" || payload === null) {
					logError("Malformed playback-error payload: expected an object", new Error("Invalid playback-error payload"), {
						method: "handlePlaybackError",
						file: "MediaLightBox.vue",
						artifactId,
						payload
					});
					return;
				}
				if (!Object.prototype.hasOwnProperty.call(payload, "recordingId")) {
					logError("Malformed playback-error payload: recordingId is missing", new Error("Missing playback-error recordingId"), {
						method: "handlePlaybackError",
						file: "MediaLightBox.vue",
						artifactId,
						payload
					});
					return;
				}
				const recordingId = (payload as { recordingId?: unknown }).recordingId;
				if (typeof recordingId !== "string" || !recordingId) {
					logError(
						"Malformed playback-error payload: recordingId must be a non-empty string",
						new Error("Invalid playback-error recordingId"),
						{
							method: "handlePlaybackError",
							file: "MediaLightBox.vue",
							artifactId,
							recordingId,
							payload
						}
					);
					return;
				}
				if (recordingId !== artifactId) {
					logInfo("Playback error ignored because recordingId does not match the current artifact", {
						method: "handlePlaybackError",
						file: "MediaLightBox.vue",
						artifactId,
						recordingId,
						payload
					});
					return;
				}
				if (this.playbackRetriedArtifactIds.has(artifactId)) {
					logInfo("Playback error ignored because recovery was already requested for the artifact", {
						method: "handlePlaybackError",
						file: "MediaLightBox.vue",
						artifactId,
						payload
					});
					return;
				}

				this.playbackRetriedArtifactIds.add(artifactId);
				this.pendingPlaybackHealUrls.set(artifactId, this.currentArtifactUrl);
				this.pendingPlaybackHealVersion += 1;
				const failedUrlAtRecoveryStart = this.currentArtifactUrl;

				const conversationId =
					typeof this.currentArtifact?.conversationId === "string"
						? this.currentArtifact.conversationId.trim()
						: this.currentArtifact?.conversationId;
				const legacyId = normalizeIdentifier(this.currentArtifact?.legacyId);
				const candidateMasterRecordingId = normalizeIdentifier(this.currentArtifact?.MasterRecording?.id);
				try {
					const refreshedRecordings = (await api.getRecordings(conversationId)) as PlaybackRecoveryRecording[];
					if (this.playbackRecoverySessionId !== recoverySessionId) {
						logInfo("Playback recovery skipped because the lightbox session changed", {
							method: "handlePlaybackError",
							file: "MediaLightBox.vue",
							artifactId,
							legacyId,
							conversationId,
							recoverySessionId,
							currentRecoverySessionId: this.playbackRecoverySessionId
						});
						return;
					}
					const refreshedRecording =
						refreshedRecordings.find(
							(candidate) => legacyId !== undefined && normalizeIdentifier(candidate?.id) === legacyId
						) ??
						refreshedRecordings.find(
							(candidate) =>
								(legacyId !== undefined && normalizeIdentifier(candidate?.MasterRecording?.id) === legacyId) ||
								(candidateMasterRecordingId !== undefined &&
									normalizeIdentifier(candidate?.id) === candidateMasterRecordingId)
						);
					if (!refreshedRecording) {
						logError(
							"Playback recovery could not find the failed recording in the refreshed response",
							new Error("Recording not found"),
							{
								method: "handlePlaybackError",
								file: "MediaLightBox.vue",
								artifactId,
								legacyId,
								candidateMasterRecordingId,
								conversationId
							}
						);
						return;
					}

					const freshMediaUrl = [refreshedRecording.recordingUrl, refreshedRecording.mediaUrl, refreshedRecording.url]
						.find((candidate): candidate is string => typeof candidate === "string" && candidate.trim().length > 0)
						?.trim();
					if (typeof freshMediaUrl !== "string" || !freshMediaUrl.trim()) {
						logError(
							"Playback recovery found the recording without a usable media URL",
							new Error("Missing refreshed media URL"),
							{
								method: "handlePlaybackError",
								file: "MediaLightBox.vue",
								artifactId,
								legacyId,
								conversationId
							}
						);
						return;
					}
					const storedFailedUrl = this.pendingPlaybackHealUrls.get(artifactId);
					if (freshMediaUrl === (typeof storedFailedUrl === "string" ? storedFailedUrl.trim() : storedFailedUrl)) {
						logInfo("Playback recovery skipped because the refreshed media URL is unchanged", {
							method: "handlePlaybackError",
							file: "MediaLightBox.vue",
							artifactId,
							legacyId,
							conversationId,
							freshMediaUrl
						});
						return;
					}
					const freshThumbnailUrl = [refreshedRecording.thumbnailUrl, refreshedRecording.privateThumbnailUrl]
						.find((candidate): candidate is string => typeof candidate === "string" && candidate.trim().length > 0)
						?.trim();
					if (!freshThumbnailUrl && (refreshedRecording.thumbnailUrl != null || refreshedRecording.privateThumbnailUrl != null)) {
						logInfo("Playback recovery ignored a malformed refreshed thumbnail URL", {
							method: "handlePlaybackError",
							file: "MediaLightBox.vue",
							artifactId,
							legacyId,
							conversationId
						});
					}
					const previousModelWrite = this.playbackHealModelWriteQueue;
					let releaseModelWrite!: () => void;
					this.playbackHealModelWriteQueue = markRaw(
						new Promise<void>((resolve) => {
							releaseModelWrite = resolve;
						})
					);
					await previousModelWrite;
					// A non-recovery writer may emit while this recovery is waiting. Let the
					// parent reconcile that v-model update before deriving our field merge.
					await this.$nextTick();
					try {
						if (this.playbackRecoverySessionId !== recoverySessionId) {
							logInfo("Playback recovery skipped because the lightbox session changed", {
								method: "handlePlaybackError",
								file: "MediaLightBox.vue",
								artifactId,
								legacyId,
								conversationId,
								recoverySessionId,
								currentRecoverySessionId: this.playbackRecoverySessionId
							});
							return;
						}
						const liveMediaItem = this.mediaItems.find((document) => (document.searchResultId ?? document.id) === artifactId);
						const liveMediaUrl = liveMediaItem?.mediaUrl;
						const normalizedLiveMediaUrl = typeof liveMediaUrl === "string" ? liveMediaUrl.trim() : liveMediaUrl;
						const normalizedFailedUrlAtRecoveryStart =
							typeof failedUrlAtRecoveryStart === "string" ? failedUrlAtRecoveryStart.trim() : failedUrlAtRecoveryStart;
						const liveMediaUrlIsWhitespaceNoise =
							typeof liveMediaUrl === "string" && liveMediaUrl.length > 0 && normalizedLiveMediaUrl.length === 0;
						if (!liveMediaUrlIsWhitespaceNoise && normalizedLiveMediaUrl !== normalizedFailedUrlAtRecoveryStart) {
							logInfo("Playback recovery skipped because the artifact's media URL already changed since the failure", {
								method: "handlePlaybackError",
								file: "MediaLightBox.vue",
								artifactId,
								legacyId,
								conversationId,
								recoverySessionId,
								failedUrlAtRecoveryStart,
								liveMediaUrl: liveMediaItem?.mediaUrl
							});
							return;
						}
						if (!this.pendingPlaybackHealUrls.has(artifactId)) {
							this.pendingPlaybackHealUrls.set(artifactId, failedUrlAtRecoveryStart);
							this.pendingPlaybackHealVersion += 1;
						}
						this.overrideMediaItemFields(artifactId, {
							mediaUrl: freshMediaUrl,
							...(freshThumbnailUrl ? { thumbnailUrl: freshThumbnailUrl } : {})
						});
						await this.$nextTick();
					} finally {
						releaseModelWrite();
					}
				} catch (error: unknown) {
					if (isAuthRefreshFailure(error)) {
						logInfo("Playback recovery stopped because the recording refresh authorization failed", {
							method: "handlePlaybackError",
							file: "MediaLightBox.vue",
							artifactId,
							legacyId,
							conversationId
						});
						auth._emitAuthError({ terminal: true });
						return;
					}
					logError("Failed to refresh recording URL for playback recovery", error, {
						method: "handlePlaybackError",
						file: "MediaLightBox.vue",
						artifactId,
						legacyId,
						conversationId
					});
				}
			},
			// LSP-16827: the "video still processing" Refresh button. Show an immediate
			// loading state so a tap (especially on mobile) visibly registers, then ask
			// the parent to reload the media list. The `artifacts` watcher clears the
			// spinner when fresh media arrives; `_refreshFeedbackTimer` is a fallback so
			// it never sticks if the list comes back unchanged (still processing).
			refreshProcessingMedia() {
				if (this.isRefreshing) return;
				this.isRefreshing = true;
				if (this._refreshFeedbackTimer) {
					clearTimeout(this._refreshFeedbackTimer);
				}
				this._refreshFeedbackTimer = window.setTimeout(() => {
					this.isRefreshing = false;
					this._refreshFeedbackTimer = null;
				}, 5000);
				this.loadRecordings();
			},
			_stopRefreshFeedback() {
				if (this._refreshFeedbackTimer) {
					clearTimeout(this._refreshFeedbackTimer);
					this._refreshFeedbackTimer = null;
				}
				this.isRefreshing = false;
			},
			handleKeydown(event: KeyboardEvent) {
				if (!this.currentArtifact) return;
				if (event.altKey || event.ctrlKey || event.metaKey) return;

				// Nested dialogs handle their own Escape on the bubble phase — leave them alone.
				// LSP-16590: the small-screen comments panel is full-screen and nothing else handles
				// its Escape, so let Escape through while it's open (resolved in the Escape chain below).
				if (this.annotating) {
					if (event.key === "Escape" && this.savingAnnotation) {
						event.preventDefault();
						this.showAnnotationSavePendingFeedback();
					}
					return;
				}
				if (this.tagDialogVisible || this.clipDialogVisible || this.dateEditDialogVisible) return;
				if (this.commenting && !(this.isMobile && event.key === "Escape")) return;

				const activeEl = document.activeElement;
				const isInputFocused =
					!!activeEl &&
					(activeEl.tagName === "INPUT" || activeEl.tagName === "TEXTAREA" || (activeEl as HTMLElement).isContentEditable);

				if (event.key === "Escape") {
					// Escape priority inside the lightbox —
					//   1. focused input/textarea → blur (unfocus the field, leave everything else alone)
					//   2. reply view open        → close that view (mimics clicking the X in CommentAction)
					//   3. comments panel open on a small screen (LSP-16590) → close it, back to the lightbox
					//   4. otherwise              → close the lightbox
					if (isInputFocused) {
						(activeEl as HTMLElement).blur();
						event.preventDefault();
						return;
					}
					if (this.replyBoxOpen) {
						EventBus.$emit("CloseReplies");
						event.preventDefault();
						return;
					}
					if (this.isMobile && this.commenting) {
						event.preventDefault();
						this.startComment();
						return;
					}
					event.preventDefault();
					this.closeLightBox();
					return;
				}

				// Arrow nav is blocked when the reply view is open or when an input is focused.
				if (this.replyBoxOpen || isInputFocused) return;

				if (event.key === "ArrowLeft") {
					event.preventDefault();
					if (this.currentArtifactIndex > 0) this.previousArtifact();
				} else if (event.key === "ArrowRight") {
					event.preventDefault();
					if (this.currentArtifactIndex < this.artifactList.length - 1) this.nextArtifact();
				}
			},
			previousArtifact() {
				const currentIndex = this.currentArtifactIndex;
				if (this.artifactList.length <= 1) return;
				const newIndex = currentIndex > 0 ? currentIndex - 1 : this.artifactList.length - 1;
				const previousArtifact = this.artifactList[newIndex];
				this.currentArtifactId = previousArtifact.id;
				this.currentArtifact = previousArtifact;
				this.currentArtifactUrl = previousArtifact.mediaUrl;
			},
			nextArtifact() {
				const currentIndex = this.currentArtifactIndex;
				if (this.artifactList.length <= 1) return;
				const newIndex = currentIndex < this.artifactList.length - 1 ? currentIndex + 1 : 0;
				const nextArtifact = this.artifactList[newIndex];
				this.currentArtifactId = nextArtifact.id;
				this.currentArtifact = nextArtifact;
				this.currentArtifactUrl = nextArtifact.mediaUrl;
			},
			// Shared by notes/tags/save/snapshot flows — the default wording describes an in-place update.
			// Callers that create something else (saveAnnotation's new edited copy) pass their own message.
			showDefaultSavingToastMessage(message: string = "Save successful. It will take a few minutes to see your updates.") {
				if (this.toast) {
					if (this.lastSaveToastId != null) {
						this.toast.dismiss(this.lastSaveToastId);
					}
					this.lastSaveToastId = this.toast.success(message);
				}
			},
			share(artifactId: string | null) {
				if (!artifactId) {
					return;
				}

				this.$emit(
					"share",
					artifactId,
					this.currentArtifact?.conversationId,
					this.artifactType === "snapshot" ? ArtifactType.Snapshots : ArtifactType.Recordings
				);
			},
			updateShowThumbs(showThumbs: boolean) {
				this.showThumbs = showThumbs;
			},
			getUserInitials() {
				return getAgentNameInitials(this.user?.name) ?? "-";
			},
			showAnnotationSavePendingFeedback() {
				if (this.annotationSavePendingFeedbackShown) {
					return;
				}

				this.annotationSavePendingFeedbackShown = true;
				const message = "Your changes are still saving. Please wait before closing.";
				this.toast.info(message, { timeout: calculateTimeout(message) });
			},
			closeLightBox() {
				// The visible close controls are hidden while annotating, but this component method is also
				// callable by sibling/host paths. Keep the terminal cleanup safe by construction: the
				// in-flight save continuation still owns currentArtifact and its optimistic annotation state.
				if (this.savingAnnotation) {
					this.showAnnotationSavePendingFeedback();
					console.warn("closeLightBox: skipped while an annotation upload is pending", {
						conversationId: this.currentArtifact?.conversationId,
						snapshotId: this.currentArtifact?.id
					});
					return;
				}
				// Invalidate recovery work and guards owned by the ending session before emitting close.
				this.playbackRecoverySessionId += 1;
				this.playbackRetriedArtifactIds.clear();
				this.pendingPlaybackHealUrls.clear();
				this.pendingPlaybackHealVersion += 1;
				this.$emit("close-lightbox");

				this._stopShutterAudio();

				if (this.artifactType === "recording" && this.$refs.videoPlayer) {
					try {
						(this.$refs.videoPlayer[0] as any).pause();
					} catch (err) {
						// Best effort
					}
				}

				// Delay state cleanup until after the v-dialog close transition completes
				// to prevent media content disappearing mid-animation (causes layout flash)
				if (this._closeCleanupTimer) {
					clearTimeout(this._closeCleanupTimer);
				}
				this._closeCleanupTimer = window.setTimeout(() => {
					this._closeCleanupTimer = null;
					this.commenting = false;
					this.annotating = false;
					this.openReplyBoxIds.clear();
					this.currentArtifact = null;
					this.currentArtifactUrl = null;
					this.releaseAllTempAnnotationObjectUrls();
					// clearTempAnnotationState also clears tempArtifactPlaceholder (LSP-16940) on top of the
					// parentId/saveStartedAt pair the LSP-18159 lifecycle clears here.
					this.clearTempAnnotationState();
					this.annotationUploadIdempotencyId = null;
					this.annotationUploadStartedAt = null;
					this.snapshotUploadAttempts.clear();
					(this.$refs.snapshotNotesPanel as { close?: () => void } | undefined)?.close?.();

					if (this.showThumbs) {
						this.toggleGallery();
					}
				}, 300);
			},
			videoRecordingId(recordingId: string) {
				return `video-${recordingId}`;
			},
			videoFileName(artifact: LightBoxArtifact) {
				return generateFileName(ArtifactType.Recordings, { id: artifact.id, src: artifact.mediaUrl }, this.parentId ?? "");
			},
			toggleGallery() {
				(this.$refs.thumbnails as any)?.toggleGallery();
				blurActiveElement();
			},
			downloadArtifacts(artifactIds: string[]) {
				// Since artifacts can be mixed, emit additional information to handle proper download

				const artifacts = this.artifactList
					.filter((x) => artifactIds.includes(x.id))
					.map((x) => {
						return {
							id: x.id,
							type: x.type,
							mediaUrl: x.mediaUrl
						};
					});
				this.$emit("download-artifacts", artifacts);
			},
			deleteArtifacts(artifactIds: string[], callback: () => void) {
				this.previousArtifactIds = this.artifactList.map((x) => x.id) as string[];
				const remainingArtifacts = this.artifactList.filter((x) => !artifactIds.includes(x.id));
				this.previousArtifactId = this.currentArtifactId;

				const updatedCallBack = (deleted: boolean) => {
					if (deleted) {
						if (artifactIds.find((x) => x === this.previousArtifactId)) {
							for (let i = 0; i < artifactIds.length; i++) {
								if (artifactIds[i] !== this.previousArtifactId) {
									this.previousArtifactIds = this.previousArtifactIds.filter((x) => x !== artifactIds[i]);
								}
							}
							let index = this.artifactList.findIndex((x) => x.id === this.previousArtifactId);
							if (index > -1) {
								this.artifactList.splice(index, 1);
							}
							index = this.previousArtifactIds.findIndex((x) => x === this.previousArtifactId);
							this.previousArtifactIds.splice(index, 1);

							if (index > 0) {
								index--;
							}
							if (this.previousArtifactIds.length > 0 && this.artifactList.length > 0) {
								this.currentArtifactId = this.artifactList[index].id;
								this.currentArtifact = this.artifactList[index];
							}
							this.previousArtifactIds = [];
							this.previousArtifactId = null;
						}
						if (callback) {
							callback();
						}

						if (remainingArtifacts.length === 0) {
							this.$emit("close-lightbox");
						}
					}
				};

				this.$emit("delete-artifacts", "snapshots", artifactIds, updatedCallBack);
			},
			async artifactChanged(id: string) {
				if (!id) {
					return;
				}

				this.currentArtifact = this.artifactList.find((x) => x.id == id) as LightBoxArtifact;
				this.currentArtifactUrl = this.currentArtifact?.mediaUrl;
				this.annotating = false;
				(this.$refs.snapshotNotesPanel as any)?.close();
			},
			shouldShowIncompleteBanner(artifact: LightBoxArtifact) {
				// Delegates to the shared predicate so this and the session banner cannot drift apart — they
				// did, for one commit, and the session offered to text a customer mid-processing while this
				// stayed correctly quiet. See helpers/incompleteRecordingUpload.ts for why each branch fails
				// closed.
				//
				// The old `conversationId` requirement is gone from THIS predicate: a missing id made
				// POST /conversations/undefined/upload-reminder, so suppressing the whole banner was right
				// while visibility and action were one decision.
				//
				// cursor: the stated reason used to read "this is text now", which stopped being true the
				// moment this PR put a live button back on this surface. The conclusion still holds, for a
				// better reason: visibility and action are now SEPARATE decisions. canSendUploadReminder
				// re-requires `artifact?.conversationId` and withholds the control without one, while the
				// warning itself stays — a viewer should still learn the recording is unfinished even when
				// nobody here can act on it.
				return isIncompleteRecordingUpload(artifact, this.uploadStatus);
			},
			// LSP-16985 (follow-up): the incomplete-upload notice is now the SAME actionable banner the
			// session page uses (IncompleteUploadBanner) rather than text pointing at it. That component
			// already carries the agreed copy, the Send Reminder button, the tonal warning styling, and a
			// layout that stacks under `sm` and goes inline above it, so both this and the session page stay
			// identical at every width instead of drifting apart the way the two lightbox copies once did.
			//
			// The original objection to putting a live control here was that this carousel can span an entire
			// project's media across many conversations, so a stray click could fire a real SMS/email for the
			// wrong session. That is answered by WHICH id is passed: the banner is bound to
			// `artifact.conversationId` — the conversation that owns the recording currently on screen — not
			// to the page's entity. IncompleteUploadBanner is already built for exactly this: it keys its
			// in-flight latch, cooldown and dismissal per conversation and watches `conversationId` so the
			// carousel handing it a different recording resets the right state (see its break-it notes).
			//
			// `canReachSessionUploadReminder` is therefore gone: it existed only to decide whether it was safe
			// to tell the viewer to go and use a control somewhere else, and there is no longer anywhere else
			// to send them.
			canSendUploadReminder(artifact: LightBoxArtifact): boolean {
				// The POST requires auth, and a view-only viewer has no business issuing a customer-facing
				// message. A missing conversationId would target nothing, so it falls through to the text notice
				// rather than rendering a button that cannot work.
				//
				// ls-heroku-ci (HIGH): this used to be JUST auth/view-only/conversationId, modelled on the
				// session page's mount — but the session page only ever shows ONE org's own session, while this
				// component is also mounted from ProjectChecklists.vue and search/SearchResults.vue, which can
				// surface artifacts spanning conversations and organizations. Every other write-capable gate in
				// this file (showTags, showEdit, canEditDate, showDelete) refuses on an ended trial, and showTags
				// additionally refuses when the artifact belongs to another organization.
				//
				// This control sends a real SMS and email to a customer, so it was the LEAST guarded and most
				// consequential action in the file — strictly worse than editing a tag, which had both checks.
				// It now applies the same defence, keyed on the passed artifact rather than `currentArtifact`,
				// so each carousel item answers for itself exactly as its conversation binding does.
				// ls-heroku-ci: every fail-closed branch says WHY, once per artifact+reason.
				//
				// This matters most for the `!meOrgId` branch. `localMe` is loaded once in mounted() inside a
				// try/catch that swallows getMe() and the user fetch into one generic console.error — so a
				// single rejected getMe() leaves it `{}` for the lightbox's whole lifetime and this control
				// silently never appears again, indistinguishable from deliberate gating and with nothing to
				// correlate. That is precisely the failure class CLAUDE.md's diagnostic-logging rule exists
				// for, on a control that sends a real customer message.
				// F-02 residual: the server's own eligibility verdict wins over every local gate, because it
				// is the one that decides whether the POST can succeed at all. Scoped to the conversation the
				// verdict names — see the `uploadReminderVerdict` prop for why an absent or non-matching
				// verdict deliberately falls through to today's behavior rather than hiding the control.
				if (
					typeof artifact?.conversationId === "string" &&
					artifact.conversationId.length > 0 &&
					this.uploadReminderVerdict?.canSendUploadReminder === false &&
					this.uploadReminderVerdict.conversationId === artifact?.conversationId
				) {
					this.reportReminderGateRefusal(
						artifact,
						"server says this conversation cannot receive an upload reminder (every send path answers a non-ASYNC conversation with 422)"
					);
					return false;
				}
				if (this.hasFreeTrialEnded) {
					this.reportReminderGateRefusal(artifact, "free trial ended");
					return false;
				}

				const meOrgId = this.localMe?.organization?.id;
				// No known organization is a fail-safe "no": this resolves asynchronously, and the notice is the
				// correct thing to show while it does — but see above for why it may never resolve at all.
				if (!meOrgId) {
					// Only worth saying once the load has finished: before that this is the expected transient
					// state, not a failure. After it, an absent organization means getMe() rejected and this
					// control will stay hidden for the session — which is the case worth being loud about.
					if (this.localMeLoadSettled) {
						this.reportReminderGateRefusal(artifact, "signed-in organization unknown (localMe failed to load)");
					}
					return false;
				}
				if (this.project && this.project?.organizationId !== meOrgId) {
					this.reportReminderGateRefusal(artifact, `project organization ${this.project?.organizationId} != ${meOrgId}`);
					return false;
				}
				// ls-heroku-ci: fails CLOSED on an absent organizationId, not open.
				//
				// This used to skip entirely when the field was falsy, which for a mount site with no `project`
				// prop (SearchResults) left nothing at all guarding cross-org access to an outbound customer
				// message. Requiring it is safe because every artifact reaching this component goes through
				// mapResultToLightBoxArtifact (artifactList maps `mediaItems` through it), and that mapper now
				// always carries organizationId — a guarantee pinned in tests/unit/helpers/filters.spec.ts.
				// So an absent value means something unexpected produced this artifact, which is exactly when
				// a Send Reminder button should not be offered.
				if (!artifact?.organizationId || artifact.organizationId !== meOrgId) {
					this.reportReminderGateRefusal(artifact, `artifact organization ${artifact?.organizationId ?? "absent"} != ${meOrgId}`);
					return false;
				}
				// No MasterRecording branch, unlike showTags: mapResultToLightBoxArtifact builds MasterRecording as
				// `{ id }` alone, so such a check could never fire. cursor caught that this was true of the
				// artifact-level check too — `organizationId` was simply never mapped — which is why the mapper
				// now carries it and why a guard test pins that, rather than this reading as defence it isn't.

				// Boolean(...) rather than a bare `&&` chain, so the declared `: boolean` return type is honest.
				//
				// cursor: my earlier rationale here was wrong — `isAuthenticated` is initialised to `false` in
				// data(), never `undefined`. The real reason the wrap earns its place is `artifact?.conversationId`,
				// a STRING: a bare chain returns that string on success and `undefined` for a null artifact, so
				// the function would return neither of the two values its signature promises. Harmless for
				// `v-if`, which reads truthiness, but a caller comparing `=== true`/`=== false` takes the wrong
				// branch on both.
				const allowed = Boolean(this.isAuthenticated && !this.isViewOnly && artifact?.conversationId);
				if (!allowed) {
					this.reportReminderGateRefusal(
						artifact,
						`viewer cannot act (authenticated=${!!this.isAuthenticated}, viewOnly=${!!this.isViewOnly}, conversationId=${artifact?.conversationId ?? "none"})`
					);
				}
				return allowed;
			},
			/** Records, once per artifact+reason, why the Send Reminder control was withheld. */
			reportReminderGateRefusal(artifact: LightBoxArtifact | null | undefined, reason: string) {
				const artifactId = artifact?.id ?? "unknown-artifact";
				const key = `${artifactId}:${reason}`;
				if (this.reportedReminderGateReasons.has(key)) return;
				this.reportedReminderGateReasons.add(key);
				console.warn(
					`MediaLightBox: withholding Send Reminder — ${reason}. ArtifactId=${artifactId}, ConversationId=${artifact?.conversationId ?? "none"}`
				);
			},
			/**
			 * LSP-17772 (review high #1): resolve the CURRENT artifact's uploader identity — its
			 * parent conversation's createdBy, the exact value the server's own-uploads-only gates
			 * compare against — via the share-extended upload-status endpoint. Cached per
			 * conversation; skipped entirely for same-org viewers (their gates never read it).
			 * All can't-decide diagnostics for the external gate live here (review medium #6:
			 * side effects do not belong in a computed).
			 */
			async resolveUploaderIdentity() {
				// The immediate watcher fires before an artifact exists — that transient state is
				// expected, not a refusal worth logging (review: it produced a spurious
				// "unknown-artifact" warning on every healthy open).
				if (!this.currentArtifact) return;
				if (!this.isExternalArtifactContext) return;
				if (!this.localMe?.user?.id) {
					if (this.localMeLoadSettled) {
						this.reportExternalGateRefusal("signed-in user unknown (localMe failed to load)");
					}
					return;
				}
				const conversationId = this.currentArtifact?.conversationId;
				if (!conversationId) {
					this.reportExternalGateRefusal("artifact carries no conversation id");
					return;
				}
				if (Object.prototype.hasOwnProperty.call(this.uploaderIdsByConversationId, conversationId)) {
					if (this.uploaderIdsByConversationId[conversationId] === null) {
						this.reportExternalGateRefusal("uploader identity unavailable (upload-status lookup failed earlier)");
					}
					return;
				}
				// Single-flight (Cursor): a second overlapping call for the same conversation must
				// not start another fetch — the loser could otherwise overwrite the winner's cached
				// identity with null.
				if (this.pendingUploaderLookups.has(conversationId)) return;
				this.pendingUploaderLookups.add(conversationId);
				try {
					const status = await api.getConversationUploadStatus(conversationId);
					this.uploaderIdsByConversationId[conversationId] = status?.conversationCreatedBy ?? null;
					if (this.uploaderIdsByConversationId[conversationId] === null) {
						this.reportExternalGateRefusal("conversation has no recorded creator");
					}
				} catch (error) {
					// A TRANSIENT failure is not cached (review): leaving the key absent lets the
					// next artifact/conversation navigation retry, instead of permanently hiding
					// Rotate/Delete from the true uploader for the life of the lightbox. Only a
					// SUCCESSFUL "no recorded creator" answer is cached as null above. The refusal
					// log is deduped per artifact+reason, so retries stay quiet.
					delete this.uploaderIdsByConversationId[conversationId];
					this.reportExternalGateRefusal(`uploader identity lookup failed (${(error as Error)?.message ?? "unknown error"})`);
				} finally {
					this.pendingUploaderLookups.delete(conversationId);
				}
			},
			/**
			 * LSP-17772 (review): same convention as reportReminderGateRefusal, for the external-editor
			 * Rotate/Delete gates. A hidden control because the caller genuinely doesn't own the upload
			 * is correct and silent; a hidden control because the IDENTITY DATA needed to decide is
			 * missing (localMe failed to load, or the artifact carries no uploader) is the silent-failure
			 * hazard worth being loud about — once per artifact+reason.
			 */
			reportExternalGateRefusal(reason: string) {
				const artifactId = this.currentArtifact?.id ?? "unknown-artifact";
				const key = `${artifactId}:${reason}`;
				if (this.reportedExternalGateReasons.has(key)) return;
				this.reportedExternalGateReasons.add(key);
				// conversationId included (review) — it is the key the upload-status API logs under,
				// so the two sides of a failed lookup can be cross-referenced.
				console.warn(
					`MediaLightBox: withholding Rotate/Delete for external editor — ${reason}. ArtifactId=${artifactId}, ConversationId=${this.currentArtifact?.conversationId ?? "none"}`
				);
			},
			// Only the NON-actionable viewers reach this now (view-only, unauthenticated, or an artifact with
			// no conversation to send for). It must never tell them to press a control they cannot see or use,
			// which is the dead-advice defect this ticket exists to remove — so it names the one thing they can
			// actually act on: the original recording device.
			//
			// LSP-17986 aligned the OPENING sentence with IncompleteUploadBanner's new copy, and deliberately
			// stopped there. The banner's second sentence is a question ("Do you want to send a link…?") because
			// the banner ships the button that answers it; these viewers have no such button, so the same
			// question would be the dead advice described above. They keep an instruction they can actually
			// follow.
			incompleteUploadMessage(artifact: LightBoxArtifact): string {
				if (
					typeof artifact?.conversationId === "string" &&
					artifact.conversationId.length > 0 &&
					this.uploadReminderVerdict?.canSendUploadReminder === false &&
					this.uploadReminderVerdict.conversationId === artifact?.conversationId
				) {
					return "This recording didn’t finish uploading from the device it was taken on. Try Resend Invitation from the session menu to get your contact back on their device.";
				}
				if (artifact?.customerId) {
					return "This recording didn’t finish uploading from the device it was taken on. Please have your customer reopen the recording link.";
				}
				return "This recording didn’t finish uploading from the device it was taken on. Reopen the upload link on the device you recorded on to finish it.";
			},
			handleMouseMove() {
				if (this.showButtons) {
					return;
				}
				this.showButtons = true;
				setTimeout(() => {
					this.showButtons = true;
				}, 1000);
			},
			openDateEditDialog() {
				if (!this.currentArtifact?.createdAt || !this.isDateEditableArtifact) {
					return;
				}
				const date = new Date(this.currentArtifact.createdAt);
				const year = date.getFullYear();
				const month = String(date.getMonth() + 1).padStart(2, "0");
				const day = String(date.getDate()).padStart(2, "0");
				const hours = String(date.getHours()).padStart(2, "0");
				const minutes = String(date.getMinutes()).padStart(2, "0");
				const dateValue = `${year}-${month}-${day}T${hours}:${minutes}`;
				this.editDatePart = `${year}-${month}-${day}`;
				this.editTimeValue = `${hours}:${minutes}`;
				this.editOriginalSeconds = date.getSeconds();
				this.editOriginalMilliseconds = date.getMilliseconds();
				this.initialEditDateValue = dateValue;
				this.dateEditDialogVisible = true;
			},
			async saveArtifactDate() {
				// LSP-20010: Save is always clickable, so every condition the `disabled` binding used to
				// enforce is enforced here instead. An unchanged or invalid click is a silent no-op — the
				// field's own inline error already says what is wrong, matching the LSP-16736 sweep, which
				// returned early rather than adding a toast. The savingDate re-entry check is what keeps a
				// second click during an in-flight request from sending a duplicate update.
				// ls-heroku-ci (LSP-20011): two of these conditions surface no inline field error, so
				// without this the click is genuinely silent — CLAUDE.md -> "Diagnostic logging when
				// fixing silent failures" applies to every fail-condition in a new gate, not just the
				// one that caused an incident.
				const dateSaveBlockedBy = this.savingDate
					? "a save is already in flight"
					: !this.currentArtifact
						? "no artifact is selected"
						: !this.isDateEditableArtifact
							? "this artifact type has no editable capture date"
							: !this.isEditDateValid || !this.editDateValue
								? "the entered date/time is not valid"
								: !this.hasEditDateChanged
									? DATE_SAVE_UNCHANGED_REASON
									: null;
				if (dateSaveBlockedBy) {
					console.warn(`MediaLightBox: date save skipped — ${dateSaveBlockedBy}`, {
						artifactId: this.currentArtifactId,
						conversationId: this.currentArtifact?.conversationId,
						editDateValue: this.editDateValue,
						initialEditDateValue: this.initialEditDateValue
					});
					// Only the "nothing changed" case earns a message: every other condition either has its
					// own inline field error or means the dialog should not have been reachable at all.
					if (dateSaveBlockedBy === DATE_SAVE_UNCHANGED_REASON) {
						const message = "No changes to save.";
						this.toast.info(message, { timeout: calculateTimeout(message) });
					}
					return;
				}
				const artifactId = this.currentArtifact.id;

				const selectedDate = new Date(this.editDateValue);
				// Preserve the artifact's original sub-minute precision when the user only edits
				// date/time at minute resolution — but never push the saved timestamp past "now",
				// since the validator only checks at minute granularity.
				const restoredDate = new Date(selectedDate);
				restoredDate.setSeconds(this.editOriginalSeconds, this.editOriginalMilliseconds);
				if (restoredDate.getTime() <= Date.now()) {
					selectedDate.setSeconds(this.editOriginalSeconds, this.editOriginalMilliseconds);
				}

				const artifactType =
					this.currentMediaItem?.entityType === "snapshot"
						? "snapshots"
						: this.currentMediaItem?.entityType === "recording"
							? "recordings"
							: null;
				if (!artifactType) {
					const message = "Date updates are only available for photos and videos.";
					this.toast.error(message, { timeout: calculateTimeout(message) });
					return;
				}

				const newDate = selectedDate.toISOString();

				try {
					this.savingDate = true;
					await api.updateArtifactDate(artifactType, artifactId, newDate);
					this.overrideMediaItemFields(artifactId, { created: newDate });
					this.dateEditDialogVisible = false;
					this.$emit("date-updated");
					const message = "Date updated successfully.";
					this.toast.success(message, { timeout: calculateTimeout(message) });
				} catch (err) {
					logError("Failed to update artifact date", err, {
						method: "saveArtifactDate",
						file: "MediaLightBox.vue",
						artifactId,
						artifactType,
						isoValue: newDate
					});
					const message = getMessageFromError(err) || "Something went wrong updating the date. Please try again.";
					this.toast.error(message, { timeout: calculateTimeout(message) });
				} finally {
					this.savingDate = false;
				}
			},
			toggleReplyBox(isOpen: boolean, commentId?: string) {
				// LSP-16267: track which comments have their reply view open so `handleKeydown`
				// can divert Escape away from `closeLightBox()` while a reply view is showing.
				// When Escape fires in that state, the lightbox emits the existing `CloseReplies`
				// EventBus signal — CommentAction.vue listens and toggles its own showReplyBox
				// off, mirroring the X-button path. If `commentId` is missing (older child
				// shapes), fall through and keep the Set in a consistent on/off state.
				if (!commentId) {
					if (!isOpen) this.openReplyBoxIds.clear();
					return;
				}
				if (isOpen) this.openReplyBoxIds.add(commentId);
				else this.openReplyBoxIds.delete(commentId);
			},
			hideThumbnailTray() {
				(this.$refs.lightbox as any).showThumbs = false;
			},
			// Insert an optimistic annotation placeholder immediately after its parent in
			// `mediaItems` (or at the front when the parent is absent). Shared by the save
			// path and the watcher's re-injection path so both stay in lockstep — LSP-16940.
			insertPlaceholderAfterParent(placeholder: IndexableDocument, parentId: string | null) {
				// Idempotent: repeated watcher passes while indexing is still pending must not stack
				// duplicate placeholders (ls-heroku on #2591).
				if (this.mediaItems.some((document) => document.id === placeholder.id)) {
					return;
				}
				const items = [...this.mediaItems];
				// PG-native search rows in mediaItems (IndexableDocument) carry the artifact id in
				// searchResultId (LSP-18260 cutover) — match searchResultId ?? id like the other mediaItems
				// sites. NOTE: this pattern is ONLY valid for mediaItems; artifactList entries are
				// LightBoxArtifact, whose mapper already collapses searchResultId into .id (ls-heroku #2591).
				const parentIndex = items.findIndex((document) => (document.searchResultId ?? document.id) === parentId);
				if (parentIndex >= 0) {
					items.splice(parentIndex + 1, 0, placeholder);
				} else {
					items.unshift(placeholder);
				}
				this.mediaItems = items;
			},
			// Drop all optimistic-annotation reconciliation state so a stale value cannot
			// leak into the next save. Deliberately NOT called by the re-injection branch,
			// which keeps the state alive until the real child indexes — LSP-16940.
			clearTempAnnotationState() {
				this.tempArtifactParentId = null;
				this.tempArtifactSaveStartedAt = null;
				this.tempArtifactPlaceholder = null;
			},
			/**
			 * LSP-21125: read the caption lines saved for the current recording, once per MasterRecording.
			 * The result is cached under the id it was requested for, and rendering reads the CURRENT id's entry,
			 * so a slow response for a recording the viewer already left can never surface on another one.
			 * Every way this ends without a toggle logs, because "why is there no Translation button" is
			 * otherwise unanswerable (CLAUDE.md "Diagnostic logging when fixing silent failures").
			 */
			async loadCurrentRecordingCaptions() {
				// Flag off (the default): never read captions, so no toggle and no panel. Expected, so not logged.
				if (!this.liveTranslationsEnabled) return;
				if (this.artifactType !== "recording") return;
				const artifactId = this.currentArtifact?.id;
				const masterRecordingId = this.currentMasterRecordingId;
				if (!masterRecordingId) {
					if (artifactId && !this.reportedMissingMasterRecordingArtifactIds.has(artifactId)) {
						this.reportedMissingMasterRecordingArtifactIds.add(artifactId);
						console.info("[RecordingTranslation] recording has no MasterRecording id; no Translation toggle", {
							artifactId,
							conversationId: this.currentArtifact?.conversationId
						});
					}
					return;
				}
				if (
					masterRecordingId in this.recordingCaptionsByMasterRecordingId ||
					this.pendingRecordingCaptionIds.has(masterRecordingId)
				) {
					return;
				}
				this.pendingRecordingCaptionIds.add(masterRecordingId);
				let captions: RecordingCaptionsResponse | null = null;
				try {
					captions = await api.getRecordingCaptions(masterRecordingId);
				} catch (error) {
					const failure = error as { httpStatus?: unknown; message?: unknown } | null;
					// Only a definitive answer is cached: a 404 means this recording has no captions. A transient failure
					// (network, 5xx, an expired token) is not, so showing the recording again retries it rather than hiding
					// the toggle for the rest of the session (AI review, PR #5941).
					const definitive = failure?.httpStatus === 404;
					console.warn(
						definitive
							? "[RecordingTranslation] captions read failed; treating the recording as having no lines"
							: "[RecordingTranslation] captions read failed; not cached, retried when the recording is shown again",
						{
							masterRecordingId,
							artifactId,
							conversationId: this.currentArtifact?.conversationId,
							httpStatus: failure?.httpStatus ?? null,
							message: typeof failure?.message === "string" ? failure.message : String(error)
						}
					);
					if (!definitive) return; // `finally` still clears the pending id
				} finally {
					this.pendingRecordingCaptionIds.delete(masterRecordingId);
				}
				this.recordingCaptionsByMasterRecordingId = {
					...this.recordingCaptionsByMasterRecordingId,
					[masterRecordingId]: hasCaptionLines(captions) ? captions : null
				};
				if (this.currentMasterRecordingId !== masterRecordingId) {
					console.info("[RecordingTranslation] captions arrived after the viewer moved on; cached, not shown", {
						masterRecordingId,
						currentMasterRecordingId: this.currentMasterRecordingId
					});
				}
			},
			toggleTranslationPanel() {
				this.translationPanelOpen = !this.translationPanelOpen;
				if (!this.translationPanelOpen) return;
				// One occupant for the right column at a time: the debug-only Capture Evidence panel yields.
				this.captureEvidenceExpanded = false;
				// Phone width: the panel and the Comments overlay never show together.
				if (this.isMobile) this.commenting = false;
			},
			// LSP-21125 QA rework (Oliver): the panel's X goes back to Comments on desktop, which shows Comments in the
			// same column as soon as the panel closes. At phone width Comments is its own overlay, and opening it from
			// the X read as the X opening something else (Jake, 2026-10-21): there the X only closes the panel.
			closeTranslationPanel() {
				this.translationPanelOpen = false;
			},
			currentVideoPlayer(): { seekTo?: (seconds: number) => void; currentTime?: unknown } | undefined {
				const videoPlayerRef = this.$refs.videoPlayer as unknown;
				return (Array.isArray(videoPlayerRef) ? videoPlayerRef[0] : videoPlayerRef) as
					| { seekTo?: (seconds: number) => void; currentTime?: unknown }
					| undefined;
			},
			onRecordingTimeUpdate(seconds: number) {
				if (!this.translationPanelVisible) return;
				this.recordingPlaybackSeconds = seconds;
			},
			seekCurrentRecording(seconds: number) {
				const videoPlayer = this.currentVideoPlayer();
				if (typeof videoPlayer?.seekTo !== "function") {
					console.warn("[RecordingTranslation] cannot seek: the video player is not mounted", {
						artifactId: this.currentArtifact?.id,
						seconds
					});
					return;
				}
				videoPlayer.seekTo(seconds);
			},
			setCurrentArtifactId(id: string) {
				this.currentArtifact = this.artifactList.find((x) => x.id == id) as LightBoxArtifact;
				this.currentArtifactId = id;
				this.currentArtifactUrl = this.currentArtifact?.mediaUrl;
				this.setCarouselAnimation();
			},
			openLightBox(id: string) {
				if (!id || !this.artifactList || this.artifactList.length === 0) {
					return;
				}

				this.resetCarouselAnimation();
				this.lightBoxIndex = this.artifactList.findIndex((s) => s.id === id);
				if (this.lightBoxIndex < 0) {
					return;
				}
				// A successful open starts a distinct epoch and clears guards owned by the previous session.
				this.playbackRecoverySessionId += 1;
				this.playbackRetriedArtifactIds.clear();
				this.pendingPlaybackHealUrls.clear();
				this.pendingPlaybackHealVersion += 1;

				// Cancel pending close cleanup only when we are actually reopening
				if (this._closeCleanupTimer) {
					clearTimeout(this._closeCleanupTimer);
					this._closeCleanupTimer = null;
				}
				this.lightBoxOpen = true;
				this.onLightBoxChange(this.lightBoxIndex);

				if (DeviceUtility.isMobile || DeviceUtility.getDeviceType() === "tablet") {
					(this.$refs.lightbox as any).canZoom = false;
				}

				// if we are in mobile then we have to close the thumbnail tray after selecting a new image
				if (this.isLightboxThumbsOpen && window.innerWidth < 960) {
					(this.$refs.lightbox as any).showThumbs = false;
				}
			},
			resetCarouselAnimation() {
				this.carouselTransition = null;
				this.carouselTransitionReverse = null;
			},
			setCarouselAnimation() {
				this.carouselTransition = "v-carousel-transition";
				this.carouselTransitionReverse = "v-carousel-reverse-transition";
			},
			onLightBoxChange(index: number) {
				this.lightBoxIndex = index;
				this.commentBoxStyle = { display: "none" };
			},
			startComment() {
				this.commenting = !this.commenting;
				// LSP-21125: at phone width Comments and the Translation panel share the space under the video, so
				// opening Comments closes the panel. On desktop Comments is always on screen and the toggle is kept.
				if (this.commenting && this.isMobile) this.translationPanelOpen = false;
			},
			startAnnotation() {
				if (this.isGifSnapshot) return;

				if (this.showThumbs) {
					this.toggleGallery();
				}

				// Use currentArtifactUrl instead of reading from refs to avoid stale image issues
				// The refs may not be updated yet if the carousel hasn't finished transitioning
				if (!this.currentArtifactUrl && this.currentArtifact?.mediaUrl) {
					this.currentArtifactUrl = this.currentArtifact.mediaUrl;
				}

				// LSP-17655 review #4: never open the annotator on the low-res thumbnail. The current
				// artifact renders through <ZoomablePhoto> (no snapshot-${id} template ref), so the only
				// full-resolution source is mediaUrl. If it is still unavailable here (e.g. the photo is
				// mid-processing), falling back to the thumbnail would let a subsequent save persist that
				// low-res image over the real one. Refuse to open the editor and tell the user instead of
				// silently degrading the photo.
				if (!this.currentArtifactUrl) {
					console.warn("startAnnotation: mediaUrl unavailable; refusing to annotate to avoid persisting a low-res thumbnail", {
						artifactId: this.currentArtifactId,
						hasThumbnailUrl: Boolean(this.currentArtifact?.thumbnailUrl)
					});
					this.toast.info("This photo is still processing — you'll be able to annotate it once it's ready.");
					return;
				}

				this.annotationUploadIdempotencyId = null;
				this.annotationUploadStartedAt = null;
				this.annotating = true;
			},
			async saveAnnotation(blob: Blob, clearEditor: boolean) {
				if (this.isGifSnapshot) return;

				if (this.savingAnnotation) {
					console.warn("saveAnnotation: skipped duplicate save while an annotation upload is pending", {
						conversationId: this.currentArtifact?.conversationId,
						snapshotId: this.currentArtifact?.id
					});
					return;
				}
				if (!blob) {
					console.warn("saveAnnotation: image editor emitted an empty save payload", {
						conversationId: this.currentArtifact?.conversationId,
						snapshotId: this.currentArtifact?.id
					});
					const message = "Something went wrong preparing changes to your photo. Please try again.";
					this.toast.error(message, { timeout: calculateTimeout(message) });
					return;
				}

				let annotationSaved = false;
				let uploadCommitted = false;
				let optimisticTempId: string | null = null;
				try {
					if (blob) {
						this.savingAnnotation = true;
						const artifact = this.currentArtifact;
						if (!artifact?.conversationId) {
							throw new Error("Cannot save an annotation without its conversation identity");
						}
						if (!this.annotationUploadIdempotencyId) {
							this.annotationUploadIdempotencyId = newUploadId();
							// The timestamp and idempotency key describe the same logical upload.
							// Preserve both across an ambiguous timeout so a retry that receives
							// the already-created child does not filter it out as "too old".
							this.annotationUploadStartedAt = Date.now();
						}
						await api.uploadSnapshot(artifact.conversationId, blob, artifact.id, {
							idempotencyId: this.annotationUploadIdempotencyId
						});
						uploadCommitted = true;
						// Transfer the stable logical-upload lower bound to optimistic-list
						// reconciliation only once the client observes a successful response.
						this.tempArtifactSaveStartedAt = this.annotationUploadStartedAt;
						// Annotation-specific wording (LSP-16940 AC): this save creates a NEW copy, unlike the
						// in-place notes/tags/save flows that share the default message.
						this.showDefaultSavingToastMessage(
							"Saved as a new edited copy — your original is untouched. It may take a minute for the copy to finish processing."
						);

						// Optimistic insert: build placeholder and insert via v-model. Revoke
						// only retired URLs that no rendered item still references; the active
						// placeholder remains on screen until the new temp row is installed.
						this.releaseUnreferencedRetiredTempAnnotationObjectUrls();
						const blobUrl = URL.createObjectURL(blob);
						this.retireTempAnnotationObjectUrl(this.tempAnnotationObjectUrl);
						this.tempAnnotationObjectUrl = blobUrl;
						const tempId = `temp-annotation-${Date.now()}`;
						optimisticTempId = tempId;
						const meUser = this.localMe?.user;
						const placeholder = {
							id: tempId,
							entityType: "snapshot",
							organizationId: artifact?.organizationId ?? "",
							createdBy: meUser?.id ?? null,
							created: new Date().toISOString(),
							updated: new Date().toISOString(),
							parentId: artifact?.id ?? null,
							labels: null,
							labelsFlat: null,
							number: null,
							notes: null,
							name: null,
							status: "ready",
							address: null,
							customers: null,
							customersFlat: null,
							users: meUser
								? [
										{
											id: meUser.id,
											name: `${meUser.firstname ?? ""} ${meUser.lastname ?? ""}`.trim(),
											email: meUser.email ?? null,
											phoneNumber: meUser.phoneNumber ?? null
										}
									]
								: null,
							usersFlat: null,
							comments: null,
							mediaUrl: blobUrl,
							thumbnailUrl: blobUrl,
							conversationId: artifact?.conversationId ?? null,
							visible: true,
							score: null,
							isUploadComplete: true,
							recordingType: null,
							conversation: {},
							project: {},
							customer: {},
							customerId: artifact?.customerId ?? null,
							agentAvatarColor: artifact?.agentAvatarColor
						} as IndexableDocument;

						this.insertPlaceholderAfterParent(placeholder, artifact?.id ?? null);

						this.tempArtifactParentId = artifact?.id ?? null;
						// Keep the placeholder so the reconciliation watcher can re-inject it
						// after a timeline reload wipes it (Azure index lag) — LSP-16940.
						this.tempArtifactPlaceholder = placeholder;

						// Settle the v-model round-trip and watcher pass that puts the temp
						// placeholder into `artifactList`, THEN switch `currentArtifactId` to
						// the temp id before emitting `annotation-saved`. Emitting first would
						// race the parent's immediate `loadTimeline` against the two nextTicks:
						// if the reload won, the watcher would fire while currentArtifactId
						// still pointed at the parent, skip reconciliation, and the trailing
						// setCurrentArtifactId(tempId) would land on an id no longer in the
						// refreshed list — buttons stuck.
						await this.$nextTick();
						await this.$nextTick();
						this.setCurrentArtifactId(tempId);
						this.$emit("annotation-saved");
						annotationSaved = true;
					}
				} catch (e) {
					const diagnostic = {
						method: "saveAnnotation",
						file: "MediaLightBox.vue",
						conversationId: this.currentArtifact?.conversationId,
						snapshotId: this.currentArtifact?.id,
						parentId: this.tempArtifactParentId
					};
					logError(
						uploadCommitted ? "Failed to finish annotation save after upload committed" : "Failed to save annotation",
						e,
						diagnostic
					);
					if (uploadCommitted) {
						// The durable artifact already exists. Closing and refreshing is safer than
						// leaving Retry open, which would upload the same edit a second time.
						const failedParentId = this.tempArtifactParentId;
						if (optimisticTempId) {
							this.mediaItems = this.mediaItems.filter((document) => document.id !== optimisticTempId);
						}
						this.releaseAllTempAnnotationObjectUrls();
						// clearTempAnnotationState (not two field nulls): tempArtifactPlaceholder must not
						// survive this failure path either — a stale revoked-blob placeholder left in state
						// could be re-injected by a later reconcile if the parent id is ever set again
						// (cursor bugbot on #2591, medium).
						this.clearTempAnnotationState();
						// Do NOT leave currentArtifactId pointing at the just-removed temp id: with
						// tempArtifactParentId nulled, the artifacts watcher's reconcile gate can never fire again
						// for it, silently stranding the user on a dead artifact (ls-heroku on #2591, HIGH —
						// reachable when the annotation-saved emit throws after the upload committed).
						if (optimisticTempId && this.currentArtifactId === optimisticTempId) {
							const parentStillPresent =
								failedParentId != null && this.artifactList.some((entry) => entry.id === failedParentId);
							if (parentStillPresent) {
								this.setCurrentArtifactId(failedParentId!);
							} else {
								this.$emit("close-lightbox");
							}
						}
						this.$emit("annotation-saved");
						annotationSaved = true;
						return;
					}
					// No optimistic placeholder was inserted on this path, so there is
					// nothing to reconcile — drop any prior value so it cannot leak into
					// the next save.
					this.clearTempAnnotationState();
					const message = "Something went wrong saving changes to your photo. Please try again.";
					this.toast.error(message, { timeout: calculateTimeout(message) });
				} finally {
					if (annotationSaved) {
						this.cancelAnnotation();
						await this.$nextTick();
						this.releaseUnreferencedRetiredTempAnnotationObjectUrls();
					} else {
						this.savingAnnotation = false;
					}
					this.applyPendingRemoteArtifactRemovals();
				}
			},
			// ls-heroku-ci (LSP-20011): the unchanged Save gives the SAME neutral answer as the date
			// dialog's — a message, and the editor stays where it is. Closing it would read as "saved"
			// on an editor that deliberately uploaded nothing, which is the ambiguity always-enabled
			// actions exist to remove. Nothing is discarded: there is nothing to discard.
			reportAnnotationUnchanged() {
				const message = "No changes to save.";
				this.toast.info(message, { timeout: calculateTimeout(message) });
			},
			cancelAnnotation() {
				this.annotating = false;
				this.savingAnnotation = false;
				this.annotationUploadIdempotencyId = null;
				this.annotationUploadStartedAt = null;
				this.currentArtifactUrl = null;
			},
			async downloadArtifact() {
				const artifact = this.currentArtifact;

				if (!artifact) {
					return;
				}

				let artifactToDownload = {
					...artifact,
					src: artifact.mediaUrl
				};

				const artifactType = artifact.type === "snapshot" ? ArtifactType.Snapshots : ArtifactType.Recordings;

				const fileName = generateFileName(artifactType, artifactToDownload, artifactToDownload.conversationId);
				this.downloadingArtifact = true;
				try {
					await downloadFile(artifactToDownload.mediaUrl, fileName);
				} catch {
					// downloadFile handles its own errors internally
				} finally {
					this.downloadingArtifact = false;
				}
			},
			editArtifactNotes() {
				if (this.isGifSnapshot) return;

				const artifact = this.currentArtifact;

				if (!artifact) {
					return;
				}

				this.currentArtifactId = artifact.id;
				this.artifactNotes = artifact?.notes;
				(this.$refs.snapshotNotesPanel as any).open();
			},
			updateArtifactNotes(notes: string) {
				const artifactId = this.currentArtifactId;

				if (!artifactId) {
					return;
				}
				this.overrideMediaItemFields(artifactId, { notes });
				// LSP-17725: SnapshotNotes already awaited `api.updateSnapshot` before emitting, and the
				// override above lands instantly, so the shared "it will take a few minutes" copy was
				// false on this path. Confirm immediately, and only after the caption is actually applied.
				// "Caption" — not "notes" — because that is what the field's own label calls it.
				this.showDefaultSavingToastMessage("Caption saved.");
			},
			async deleteArtifact() {
				if (this.hasFreeTrialEnded) {
					return;
				}

				// LSP-19254: the gate above only hides the buttons. Re-check it here so the destructive call
				// itself is guarded — a keyboard shortcut, a bulk action or a direct call would otherwise
				// reopen the orphaned-stream bug without going anywhere near `showDelete` (ls-heroku-ci
				// review). Both current callers are template-gated, so this is unreachable today by design.
				if (this.deleteGateDecision.withheld) {
					// The SAME fields the watcher logs. This is the enforcing call site, not the observational
					// one, so it is the line a triager reads first — and for the ambiguous PENDING branches
					// the upload verdicts are what explain the refusal at all (ls-heroku-ci).
					console.warn("MediaLightBox.deleteArtifact: refused — the recording may still be written", {
						reason: this.deleteGateDecision.reason,
						recordingId: this.currentArtifact?.id,
						conversationId: this.currentArtifact?.conversationId,
						status: this.currentArtifact?.status,
						recordingType: this.currentArtifact?.recordingType,
						isUploadComplete: this.currentArtifact?.isUploadComplete,
						isUploadAbandoned: this.currentArtifact?.isUploadAbandoned
					});
					return;
				}

				let index = this.artifactList.findIndex((obj) => obj.id === this.currentArtifactId);
				const artifactType = this.currentArtifact?.type === "snapshot" ? "photo" : "recording";
				// TODO: Update message based on artifact type
				if (
					await (this.$root as any).$confirm.deleteOpen(
						`Delete Media`,
						`Are you sure? This media may be attached to a checklist. It will be deleted in all places.`
					)
				) {
					try {
						if (this.currentArtifact?.type === "snapshot") {
							await (api as any).deleteSnapshot(this.currentArtifact?.id);
						} else {
							await (api as any).deleteRecording(this.currentArtifact?.conversationId, this.currentArtifact?.id);
						}

						this.$emit("artifact-deleted", this.currentArtifact?.id);

						this.artifactList.splice(index, 1);
						let artifactListLength = this.artifactList.length;
						if (artifactListLength > 0) {
							// Ensure index is valid after deletion
							if (index >= artifactListLength) index = artifactListLength - 1;

							this.currentArtifact = this.artifactList[index];
							this.currentArtifactId = this.currentArtifact?.id;
							this.currentArtifactUrl = this.currentArtifact?.mediaUrl;
						} else {
							this.$emit("close-lightbox");
						}
					} catch (error) {
						logError("Failed to delete artifact", error, {
							method: "deleteArtifact",
							file: "MediaLightBox.vue",
							artifactId: this.currentArtifactId,
							artifactType,
							conversationId: this.currentArtifact?.conversationId
						});
						const message = `Something went wrong deleting the ${artifactType}. Please try again.`;
						this.toast.error(message, { timeout: calculateTimeout(message) });
					}
				}
			},
			// LSP-15266 — remote-delete entry point. Splice the artifact out of
			// the lightbox in place, and if it was the current one, advance to
			// the next/previous item rather than slamming the lightbox closed.
			// Only emits close-lightbox when the deleted artifact was the last
			// one left. Mirrors deleteArtifact()'s post-splice navigation but
			// skips the user confirmation + API call (the delete already
			// happened on another client). Returns true if the lightbox handled
			// the delete (artifact was in the list).
			removeArtifactById(id: string): boolean {
				let index = this.artifactList.findIndex((artifact) => artifact.id === id);
				if (index === -1) return false;

				// Remote-delete notifications can arrive while this component still owns an
				// annotation upload. Removing the current artifact here can emit close-lightbox
				// and unmount the upload continuation out from under its captured identity.
				if (this.savingAnnotation) {
					this.pendingRemoteArtifactRemovalIds.add(id);
					console.warn("removeArtifactById: queued while an annotation upload is pending", {
						artifactId: id,
						conversationId: this.currentArtifact?.conversationId,
						snapshotId: this.currentArtifact?.id
					});
					// This component accepted ownership and will apply the removal when the
					// save settles, so the caller may surface its remote-delete toast now.
					return true;
				}
				const wasCurrent = this.currentArtifactId === id;
				this.artifactList.splice(index, 1);
				this.$emit("artifact-deleted", id);

				if (!wasCurrent) return true;

				if (this.artifactList.length === 0) {
					this.$emit("close-lightbox");
					return true;
				}

				if (index >= this.artifactList.length) index = this.artifactList.length - 1;
				const nextArtifact = this.artifactList[index];
				this.currentArtifact = nextArtifact;
				this.currentArtifactId = nextArtifact.id;
				this.currentArtifactUrl = nextArtifact.mediaUrl;
				return true;
			},
			applyPendingRemoteArtifactRemovals() {
				const pendingArtifactIds = [...this.pendingRemoteArtifactRemovalIds];
				this.pendingRemoteArtifactRemovalIds.clear();
				for (const artifactId of pendingArtifactIds) {
					this.removeArtifactById(artifactId);
				}
			},
			reset() {
				this.currentArtifactId = null;
				this.currentArtifact = null;
			},
			save() {
				this.saving = true;
				this.$store.dispatch("saveSnapshots", this.snapshotList).then(() => {
					this.saving = false;
					this.dirty = false;
					this.showDefaultSavingToastMessage();
				});
			},
			// TODO: Update based on artifact type
			async saveTags(tags: Label[]) {
				if (this.savingTags) {
					return;
				}
				const currentArtifact = this.currentArtifact;

				if (!currentArtifact) {
					return;
				}

				const url = currentArtifact.type === "snapshot" ? "master-documents" : "master-recordings";

				if (currentArtifact.type === "snapshot") {
					if (!currentArtifact.MasterDocument) {
						currentArtifact.MasterDocument = {
							documentRelatedEntity: "snapshot"
						} as Partial<MasterDocument>;
					}
				} else {
					if (!currentArtifact.MasterRecording) {
						currentArtifact.MasterRecording = {
							recordingType: "async"
						} as Partial<MasterRecording>;
					}
				}

				this.savingTags = true;
				try {
					const labels = await api.updateTagsForEntity(url, currentArtifact.id, { ...currentArtifact, Labels: tags });
					this.overrideMediaItemFields(currentArtifact.id, { labels });
					this.showDefaultSavingToastMessage();
				} catch (err) {
					logError("Failed to save tags for artifact", err, {
						method: "saveTags",
						file: "MediaLightBox.vue",
						artifactId: currentArtifact.id,
						artifactType: currentArtifact.type,
						tagCount: tags?.length
					});
					const message = "Failed to save tags.";
					this.toast.error(message, { timeout: calculateTimeout(message) });
				} finally {
					this.savingTags = false;
					this.tagDialogVisible = false;
				}
			},

			_stopShutterAudio() {
				if (this._shutterAudio) {
					this._shutterAudio.pause();
					this._shutterAudio.currentTime = 0;
					this._shutterAudio = null;
				}
			},

			async takeSnapshot(videoElement: HTMLVideoElement, recordingId: string) {
				if (this._snapshotInProgress) {
					return;
				}
				this._snapshotInProgress = true;

				let conversationId: string | undefined;
				let projectId = "";
				let snapshotUploadAttemptKey: string | undefined;
				// Bugbot/QA (PR #4816/#4819 investigation): `_snapshotInProgress` only needs to guard the
				// synchronous frame-capture step (grabbing the current video frame and computing the
				// dedup key) against a rapid double-click racing itself. It used to stay `true` for the
				// entire network upload + server receipt-confirmation round trip too (via the awaited
				// `api.uploadSnapshot(...)` below), which can legitimately take 20+ seconds on a slow
				// upload lane -- silently no-opping every snapshot click during that whole window, with
				// no visible feedback that anything was ignored. Release it in a `finally` scoped to just
				// the capture phase, then let the upload continue in the background.
				try {
					conversationId = this.currentArtifact?.conversationId;
					projectId = this.currentArtifact?.projectId ?? "";

					try {
						this._stopShutterAudio();
						const audio = new Audio(CameraShutterClickUrl);
						this._shutterAudio = audio;
						audio.play().catch(() => {});
					} catch (err) {
						console.error(err);
					}

					if (!conversationId) {
						if (this.toast) {
							this.toast.error(this.uploadSnapshotErrorMessage, {
								timeout: calculateTimeout(this.uploadSnapshotErrorMessage)
							});
						}
						return;
					}

					const sourceMasterRecordingId = this.currentArtifact?.MasterRecording?.id;
					// A clip and its source recording can share one master-recording identity while
					// showing different pixels/playheads. Retain an ambiguous retry only for the exact
					// lightbox artifact and rounded capture millisecond on which the capture began.
					const snapshotUploadScope = `${conversationId}:${recordingId}:${sourceMasterRecordingId ?? ""}`;
					const captureOffsetMs = Math.round(videoElement.currentTime * 1000);
					snapshotUploadAttemptKey = `${snapshotUploadScope}:${captureOffsetMs}`;
					if (!this.snapshotUploadAttempts.has(snapshotUploadAttemptKey)) {
						const blob = await SnapshotHelper.takeSnapshot(
							videoElement,
							videoElement.videoWidth,
							videoElement.videoHeight,
							false
						);
						this.snapshotUploadAttempts.set(snapshotUploadAttemptKey, {
							blob,
							captureOffsetMs,
							metadata: {
								projectId: `${projectId}`,
								description: "",
								idempotencyId: newUploadId(),
								...(sourceMasterRecordingId
									? {
											sourceMasterRecordingId,
											sourceMasterRecordingOffsetMs: captureOffsetMs
										}
									: {})
							},
							uploadPromise: undefined
						});
					}
				} catch (err) {
					logError("Failed to capture snapshot frame", err, {
						method: "takeSnapshot",
						file: "MediaLightBox.vue",
						conversationId,
						projectId,
						recordingId
					});
					if (this.toast) {
						this.toast.error(this.uploadSnapshotErrorMessage, {
							timeout: calculateTimeout(this.uploadSnapshotErrorMessage)
						});
					}
					return;
				} finally {
					this._snapshotInProgress = false;
				}

				const snapshotUploadAttempt = snapshotUploadAttemptKey
					? this.snapshotUploadAttempts.get(snapshotUploadAttemptKey)
					: undefined;
				if (!snapshotUploadAttemptKey || !snapshotUploadAttempt) {
					logError("Snapshot upload attempt was not retained for the current artifact", undefined, {
						method: "takeSnapshot",
						file: "MediaLightBox.vue",
						conversationId,
						projectId,
						recordingId
					});
					if (this.toast) {
						this.toast.error(this.uploadSnapshotErrorMessage, {
							timeout: calculateTimeout(this.uploadSnapshotErrorMessage)
						});
					}
					return;
				}
				if (snapshotUploadAttempt.uploadPromise) {
					// Bugbot (PR #4816): a repeat click hit the dedup branch above (paused/stationary
					// video, or the same rounded capture millisecond) and this exact capture's upload is
					// already dispatched. Coalesce onto that SAME in-flight promise instead of calling
					// api.uploadSnapshot(...) again with the identical id/blob -- re-enqueuing an
					// already-"pending" durable row resets its retry bookkeeping and can drive a
					// duplicate presign+PUT of identical bytes mid-flight. The original dispatch's own
					// catch below already logs/toasts a failure, so swallow it here.
					await snapshotUploadAttempt.uploadPromise.catch(() => {});
					return;
				}
				const key = snapshotUploadAttemptKey;
				const uploadPromise = (async () => {
					try {
						await api.uploadSnapshot(conversationId, snapshotUploadAttempt.blob, null, snapshotUploadAttempt.metadata);
						this.snapshotUploadAttempts.delete(key);
						this.showDefaultSavingToastMessage();
					} catch (err) {
						// Keep the retained blob/identity on failure (LSP-11811: an ambiguous failure may
						// still have landed server-side, so a retry must reuse the same idempotencyId, not
						// mint a new capture) but clear `uploadPromise` -- once THIS attempt has settled,
						// a later click at the same position must dispatch a fresh retry with that same
						// identity, not coalesce onto an already-failed promise forever.
						if (this.snapshotUploadAttempts.get(key) === snapshotUploadAttempt) snapshotUploadAttempt.uploadPromise = undefined;
						logError("Failed to upload snapshot", err, {
							method: "takeSnapshot",
							file: "MediaLightBox.vue",
							conversationId,
							projectId,
							recordingId
						});
						if (this.toast) {
							this.toast.error(this.uploadSnapshotErrorMessage, {
								timeout: calculateTimeout(this.uploadSnapshotErrorMessage)
							});
						}
					}
				})();
				snapshotUploadAttempt.uploadPromise = uploadPromise;
				await uploadPromise;
			},

			onClipSaved(jobId: string) {
				this.clipDialogVisible = false;
				this.$emit("clip-saved", { jobId, conversationId: this.currentArtifact?.conversationId });
				this.$emit("close-lightbox");
			}
		}
	};
</script>

<style scoped lang="scss">
	// LSP-21125: v-show alone cannot hide these while the Translation panel is open — their roots carry
	// Vuetify's `d-flex` (display: flex !important), which beats v-show's inline display: none, so Comments
	// stayed in the column and pushed the panel below the card's clipped edge. Two classes + !important wins.
	.comment-controls .lightbox-hidden-by-translation {
		display: none !important;
	}
	.line-clamp-2 {
		-webkit-line-clamp: 2;
		line-clamp: 2;
		display: -webkit-box;
		-webkit-box-orient: vertical;
	}
	:deep(.v-window__container) {
		width: 100%;
	}
	.mobile-controls {
		width: 100%;
	}
	.snapshot-container {
		background-color: #000;
		border-radius: 12px 0px 0px 0px;
	}

	.lightbox-header {
		height: 72px;
	}

	.comment-wrapper {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		display: flex;
		flex-direction: column;
		pointer-events: none;
	}

	.add-comment-card {
		position: relative;
		bottom: 0;
	}
	.lightbox-controls {
		position: fixed;
		width: 100%;
		display: flex;
		z-index: 10000;
		left: 0%;
		padding-left: env(safe-area-inset-left);
		padding-right: env(safe-area-inset-right);
	}

	.lightbox-access {
		position: fixed;
		display: flex;
		z-index: 10000;
		right: calc(15% + env(safe-area-inset-right));
		top: calc(2% + env(safe-area-inset-top));
		color: #000;
	}

	.theme--light.v-btn.v-btn--icon {
		color: #000;
	}

	.lightbox-controls > button.theme--light.v-btn.v-btn--icon {
		color: black;
		border-radius: 100px;
		height: 40px;
		width: 40px;
		margin: 10px 3px;
		z-index: 1000000;
	}

	.lightbox-controls > button.theme--light.v-btn.v-btn--icon:last-child {
		color: #ec5d43;
	}

	.lightbox-tooltip {
		font-size: 10px;
		line-height: 12px;
		padding: 5px 8px;
	}

	.snapshots.fixed {
		position: fixed;
		top: 0;
		left: 0;
		z-index: 10010;
		width: 280px;
		height: 100vh;
		height: 100dvh;
		overflow-y: scroll;
		overscroll-behavior: none;
	}

	.snapshots.fixed button {
		background-color: #1e2128 !important;
		color: #fff;
		font-size: 12px;
	}

	.snapshots.fixed > .snapshot-controls {
		flex-direction: column;
	}

	.snapshots.fixed > .snapshot-controls > div:nth-child(2) > button {
		background-color: #000 !important;
	}

	.snapshots.fixed > .snapshot-controls > div:nth-child(2) > button:first-child {
		color: #fff !important;
	}

	@media (min-width: 960px) {
		.lightbox-access {
			right: calc(7% + env(safe-area-inset-right)) !important;
		}

		.desktop-right {
			overflow-y: auto;
			border-radius: 0px 12px 0px 0px !important;
			filter: unset !important;
			height: 100%;
			border-left: #fff;
			box-shadow: unset !important;
		}

		.lightbox-controls {
			height: 70px;
			width: 90%;
			margin-left: 5%;
			align-items: center;
		}

		.comment-controls {
			width: 350px !important;
			right: 6% !important;
			top: 11vh !important;
		}

		.comment-box {
			max-height: 100% !important;
		}
	}

	.comment-controls {
		width: 90%;
		height: 100%;
		display: flex;
		flex-direction: column;
		z-index: 10000;
	}

	.comment-controls .v-overlay {
		z-index: 10005;
		margin: 32px;
		border-radius: 12px;
	}

	/* this section is when the nav bar moves to the bottom of the screen */
	@media #{map.get($display-breakpoints, 'sm-and-down')} {
		.snapshot-container {
			border-radius: 0px;
		}

		/* Mobile carousel arrows are hidden in favor of custom navigation */

		.theme--light.v-btn.v-btn--icon {
			color: #000;
		}

		.lightbox-controls {
			justify-content: space-between;
			top: 32%;
			padding-left: 16px;
			padding-right: 16px;
		}

		.desktop-right {
			flex: 1;
			display: flex;
			flex-direction: column;
			overflow: hidden;
			border-radius: 0px 0px 0px 0px !important;
		}

		html {
			-ms-overflow-style: none;
			scrollbar-width: none;
			max-width: 100% !important;
			overflow: hidden !important;
			overflow-y: auto !important;
		}
		html ::-webkit-scrollbar {
			display: none;
		}
		.comment-controls {
			width: 100%;
			right: 0;
			height: calc(100% - 86px);
			bottom: 0px;
			padding-left: env(safe-area-inset-left, 0px);
			padding-right: env(safe-area-inset-right, 0px);
		}

		.comment-controls .v-overlay {
			margin: 10px !important;
		}

		.snapshots.fixed {
			width: 100%;
			z-index: 10009;
		}
	}

	.lightbox-container {
		background-color: #f3f5f9;
		height: 100vh;
		height: 100dvh;
		width: 100vw;
		width: 100dvw;
		overflow: auto;
		overscroll-behavior: none;
		transition: opacity 0.2s ease-in-out;
	}

	.carousel-nav-btn {
		background-color: rgba(0, 0, 0, 0.5) !important;
		color: white !important;
	}

	.carousel-nav-btn.v-btn--disabled {
		background-color: rgba(0, 0, 0, 0.2) !important;
		color: rgba(255, 255, 255, 0.5) !important;
	}


	@media #{map.get($display-breakpoints, 'sm-and-down')} {
		.recording-carousel {
			min-height: 50%;
		}
		.recording-carousel :deep(.v-window__container) {
			height: 100%;
			width: 100%;
		}
		.recording-carousel :deep(.v-window__container .v-carousel__item) {
			height: 100%;
		}
	}

	// LSP-21125: the phone-width Translation panel fills the height left under the video and controls, and its rows
	// scroll inside it, so the video stays on screen while the transcript is read. The floor keeps a few rows
	// readable on a short landscape phone (the lightbox scrolls past it there).
	.mobile-translation-panel {
		flex: 1 1 0;
		min-height: 160px;
	}

	.mobile-controls-buttons .v-btn--icon.v-btn--density-default {
		width: 32px !important;
		height: 32px !important;
	}
	.loading-overlay {
		backdrop-filter: blur(4px);
	}

	@media #{map.get($display-breakpoints, 'md')} {
		.recording-carousel :deep(.v-window__controls) {
			padding: 0 4px !important;
		}
	}

	/*
		LSP-19248: re-assert this dialog's own padding above App.vue's global
		`.v-card-text { padding: 0.5rem !important }`. Scoped compilation makes this
		`.edit-date-dialog .v-card-text.edit-date-body[data-v-hash]`, which outranks the
		global single-class rule, and `!important` is required because the global rule
		carries it too. Values mirror the `px-6 pt-2 pb-8` the markup used to ask for.
	*/
	.edit-date-dialog .v-card-text.edit-date-body {
		padding: 8px 24px 32px !important;
	}

	.edit-date-fields {
		width: 100%;
	}

	/*
		LSP-22163: Vuetify lays the month out as `repeat(7, min-content)` over fixed 40px day cells,
		a 328px minimum, but on a 375px phone this dialog leaves the picker only ~279px and
		`.v-date-picker` clips with `overflow: hidden` — the Saturday column fell off the right edge.
		Letting each column shrink (capped at the stock 40px, so wider screens are unchanged) fits all
		seven; the 36px day button stays centred and simply overlaps into the 4px column gap.
	*/
	.edit-date-dialog :deep(.v-date-picker-month__days) {
		grid-template-columns: repeat(var(--v-date-picker-days-in-week), minmax(0, 40px));
	}

	.edit-date-dialog :deep(.v-date-picker-month__day) {
		width: auto;
		min-width: 0;
	}

	.edit-date-actions {
		border-top: 1px solid rgba(0, 0, 0, 0.08);
	}
</style>
